import {
  OktonPageHeader,
  OktonInput,
  OktonCheckbox,
  OktonAlert,
  OktonDateFilter,
  OktonLoadingCard,
  OktonErrorCard,
  OktonSearchSelect,
  OktonSelect,
  OktonDataGrid,
  OktonEmptyState,
  TAMANHOS_PAGINA,
  OktonGridFooter,
  OktonConfirmDialog,
  ColunaGrid,
  filtrarPorBusca,
  useGrid,
} from '@okton/platform-ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  AlertTriangle,
  ArrowDownWideNarrow,
  ArrowLeftRight,
  ArrowUpNarrowWide,
  ArrowUpDown,
  Banknote,
  Building2,
  CheckCircle2,
  Clock,
  FileText,
  Landmark,
  Link2,
  Minus,
  PiggyBank,
  Plus,
  Search,
  Sparkles,
  Unlink,
  Upload,
  Zap,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EstadoConciliacao } from './estadoNavegacao';
import CaixaField from '../../features/caixa/CaixaField';
import BankAppPage from '../../layout/BankAppPage';
import {
  CENTRO_CUSTO_NOVO,
  CLIENTE_NOVO,
  DEPARTAMENTO_NOVO,
  DIRECIONADOR_NOVO,
  EnderecoConsultaCnpj,
  TIPO_CLIENTE_PF,
  TIPO_CLIENTE_PJ,
  useCentrosCusto,
  useClientePorDocumento,
  useClientes,
  useConsultaCnpj,
  useCreateCentroCusto,
  useCreateCliente,
  useCreateDepartamento,
  useCreateDirecionador,
  useDepartamentos,
  useDirecionadores,
  useMoedasPagamento,
  useParametros,
  usaControlaDRE,
  usePlanosDFC,
} from '../../services/hooks/useCadastros';
import { formataDocumento, soDigitos } from '../../utils/documento';
import {
  bancoComExtratoApi,
  useContasBancarias,
  useVinculosDaConta,
} from '../../services/hooks/useContasBancarias';
import {
  useChequesDisponiveis,
  useDepositos,
} from '../../services/hooks/useDepositos';
import { useSaques } from '../../services/hooks/useSaques';
import {
  pontaDaConta,
  Transferencia,
  useTransferencias,
} from '../../services/hooks/useTransferencias';
import {
  LancamentoExtrato,
  useExtratoBancario,
} from '../../services/hooks/useExtratoBancario';
import {
  LancamentoAberto,
  LancamentoFinanceiro,
  TIPO_FIN_PAGAR,
  TIPO_FIN_RECEBER,
  useLancamentosAbertos,
  useLancamentosFinanceiros,
} from '../../services/hooks/useLancamentosFinanceiros';
import {
  Conciliado,
  TIPO_FINANCEIRO,
  descreveDocumento,
  useConciliacoes,
} from '../../services/hooks/useConciliacoes';
import {
  useAutoMatch,
  useBaixarEConciliar,
  useCadastrarDeposito,
  useCadastrarSaque,
  useCadastrarTransferencia,
  useGerarFinanceiro,
  useLinkConciliados,
  useLinkDocumento,
  useLinkTransferencia,
  useUnlinkConciliados,
  useVincularVarios,
} from '../../services/hooks/useConciliadosMutations';
import formatValue from '../../utils/formatValue';
import { formatCustomDate } from '../../utils/formatCustomDate';
import { formatDataExtrato } from '../../utils/formatDataExtrato';
import { FiltroData, setaFiltroMensal } from '../../utils/setaFiltroDiario';
import { colunasDataGrid } from '../../utils/colunasDataGrid';

const TRANSFERENCIA_VAZIA = {
  documento: '',
  origem: '',
  destino: '',
  planocontas: '',
  observacao: '',
};

const GERAR_VAZIO = {
  cliente: '',
  clientenovotipo: TIPO_CLIENTE_PF,
  clientenovonome: '',
  clientenovodocumento: '',
  clientenovoie: '',
  documento: '',
  observacao: '',
  centrocusto: '',
  centrocustonovo: '',
  departamento: '',
  departamentonovo: '',
  direcionador: '',
  direcionadornovo: '',
  direcionadorplano: '',
  caixa: '',
  moeda: '',
};

const DOCUMENTO_VAZIO = {
  moeda: '',
  envelope: '',
  planocontas: '',
  caixa: '',
  observacao: '',
};

// O que a aba Cadastro cria a partir do lançamento selecionado. Antes cada um
// destes era uma aba; virou um seletor porque a coluna só cabe duas.
type TipoCadastro = 'lancamento' | 'transferencia' | 'saque' | 'deposito';

const DESCRICAO_CADASTRO: Record<TipoCadastro, string> = {
  lancamento: 'Título liquidado na Conta, com Cliente e classificação.',
  transferencia: 'Movimento entre contas próprias: não gera título.',
  saque: 'Dinheiro que sai da Conta e entra no Caixa.',
  deposito: 'Dinheiro que sai do Caixa e entra na Conta.',
};

// O valor do outro lado do vínculo: só o título tem valor próprio, nos demais
// o que vale é o que ficou gravado no vínculo.
function valorDoDocumento(m: Conciliado): number {
  return m.tipo_cbc === TIPO_FINANCEIRO
    ? Number(m.valorbaixa_fin)
    : Number(m.valor_cbc);
}

const COLUNAS_CONCILIADOS: ColunaGrid<Conciliado>[] = [
  {
    chave: 'extrato',
    titulo: 'Extrato bancário',
    valor: m => m.descricao_exb,
  },
  // Coluna do elo entre os dois lados: só o ícone, nada a ordenar.
  { chave: 'elo', titulo: '', largura: '8', px: '2' },
  {
    chave: 'documento',
    titulo: 'Lançamento financeiro',
    valor: m => descreveDocumento(m).titulo,
  },
  {
    chave: 'valor_exb',
    titulo: 'Valor banco',
    valor: m => Number(m.valor_exb),
    tipo: 'numero',
    alinhamento: 'right',
  },
  {
    chave: 'valor_doc',
    titulo: 'Valor financeiro',
    valor: m => valorDoDocumento(m),
    tipo: 'numero',
    alinhamento: 'right',
    display: ['none', 'none', 'table-cell'],
  },
];

// Sub-abas da Consulta: Baixados e Em Aberto ganharam a coluna inteira para
// si — antes dividiam a mesma coluna e sobrava pouco espaço pros dados —, e
// Saques/Depósitos passaram a dividir uma aba em duas colunas, do jeito que
// Lançamentos fazia antes.
type AbaConsulta = 'baixados' | 'aberto' | 'saquesDepositos' | 'transferencias';

const ABAS_CONSULTA: [AbaConsulta, string][] = [
  ['baixados', 'Baixados'],
  ['aberto', 'Em Aberto'],
  ['saquesDepositos', 'Saques / Depósitos'],
  ['transferencias', 'Transferências'],
];

// Texto do campo de busca de cada aba, dizendo exatamente o que ele varre —
// um "Buscar..." genérico não deixava claro o que dava pra digitar ali.
const PLACEHOLDER_BUSCA: Record<AbaConsulta, string> = {
  baixados: 'Buscar cliente, documento, valor...',
  aberto: 'Buscar cliente, documento, valor...',
  saquesDepositos: 'Buscar descrição, valor...',
  transferencias: 'Buscar observação, documento, origem, destino...',
};

// Campos que a busca de cada aba varre. Só a coluna com valor entra: sem ela
// o filtrarPorBusca não tem o que ler.
const COLUNAS_BUSCA_FINANCEIRO: ColunaGrid<LancamentoFinanceiro>[] = [
  { chave: 'observacao', titulo: 'Observação', valor: t => t.observacao_fin },
  { chave: 'documento', titulo: 'Documento', valor: t => t.documento_fin },
  { chave: 'cliente', titulo: 'Cliente', valor: t => t.nome_cli },
  {
    chave: 'valor',
    titulo: 'Valor',
    valor: t => Number(t.valorbaixa_fin),
  },
];

// Título em aberto não tem baixa: o valor que a linha mostra, e que a busca
// tem que achar, é o saldo que falta.
const COLUNAS_BUSCA_ABERTOS: ColunaGrid<LancamentoAberto>[] = [
  { chave: 'observacao', titulo: 'Observação', valor: t => t.observacao_fin },
  { chave: 'documento', titulo: 'Documento', valor: t => t.documento_fin },
  { chave: 'cliente', titulo: 'Cliente', valor: t => t.nome_cli },
  { chave: 'valor', titulo: 'Valor', valor: t => Number(t.saldo_fin) },
];

// Campos que a busca do Extrato Bancário varre.
const COLUNAS_BUSCA_EXTRATO: ColunaGrid<LancamentoExtrato>[] = [
  { chave: 'descricao', titulo: 'Descrição', valor: t => t.descricao_exb },
  { chave: 'referencia', titulo: 'Referência', valor: t => t.referencia_exb },
  { chave: 'valor', titulo: 'Valor', valor: t => Number(t.valor_exb) },
];

// Formato comum de Saque e Depósito depois de mapeados em documentosDisponiveis.
interface DocumentoDisponivel {
  tipo: 'saque' | 'deposito';
  id: string;
  data: string;
  valor: string;
  descricao: string;
  sugerido: boolean;
}

const COLUNAS_BUSCA_DOCUMENTO: ColunaGrid<DocumentoDisponivel>[] = [
  { chave: 'descricao', titulo: 'Descrição', valor: d => d.descricao },
  { chave: 'valor', titulo: 'Valor', valor: d => Number(d.valor) },
];

const COLUNAS_BUSCA_TRANSFERENCIA: ColunaGrid<Transferencia>[] = [
  { chave: 'observacao', titulo: 'Observação', valor: t => t.observacao_tba },
  { chave: 'documento', titulo: 'Documento', valor: t => t.documento_tba },
  { chave: 'origem', titulo: 'Origem', valor: t => t.origemdescricao_tba },
  { chave: 'destino', titulo: 'Destino', valor: t => t.destinodescricao_tba },
  { chave: 'valor', titulo: 'Valor', valor: t => Number(t.valor_tba) },
];

function daysDiff(a: string, b: string) {
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) / 86400000;
}

// Ordenação por valor das duas colunas. Sem ordem escolhida vale a de sempre:
// sugestão primeiro, que é o que ajuda a achar o par.
type OrdemValor = '' | 'asc' | 'desc';

function ordenaPorValor<T>(
  lista: T[],
  valorDe: (item: T) => number,
  ordem: OrdemValor,
): T[] {
  if (ordem === '') return lista;
  const sinal = ordem === 'asc' ? 1 : -1;
  return [...lista].sort((a, b) => (valorDe(a) - valorDe(b)) * sinal);
}

// Mesmo critério do auto-match do back: mesmo valor em módulo e data dentro da
// janela. Serve para tudo que pode explicar um lançamento do extrato — título
// baixado, título em aberto, Saque, Depósito ou Transferência —, e não só para
// os baixados, que era o que a sugestão enxergava.
const JANELA_SUGESTAO = 5;

function combinaComExtrato(
  valor: number | string,
  data: string | null,
  valorExtrato: number,
  dataExtrato: string,
) {
  if (!data) return false;
  return (
    Math.abs(Number(valor) - Math.abs(valorExtrato)) < 0.01 &&
    daysDiff(data.slice(0, 10), dataExtrato.slice(0, 10)) <= JANELA_SUGESTAO
  );
}

// ─── tipos locais ───
interface FinancialRowProps {
  tx: LancamentoFinanceiro;
  bankAmount: number;
  highlight: boolean;
  linking: boolean;
  onClick: () => void;
  selecionado: boolean;
  onSelecionar: () => void;
}

// ─── botão de ordenação por valor ───
// Três estados em um clique só: sem ordem (a de sempre, sugestão primeiro),
// crescente e decrescente. O ícone diz em qual deles está.
function BotaoOrdemValor({
  ordem,
  setOrdem,
}: {
  ordem: OrdemValor;
  setOrdem: (ordem: OrdemValor) => void;
}) {
  const proxima: Record<OrdemValor, OrdemValor> = {
    '': 'asc',
    asc: 'desc',
    desc: '',
  };
  const rotulo =
    ordem === 'asc'
      ? 'Ordenado por valor, do menor para o maior'
      : ordem === 'desc'
      ? 'Ordenado por valor, do maior para o menor'
      : 'Ordenar por valor';
  const corInativo = useColorModeValue('gray.600', 'gray.300');
  return (
    <Tooltip label={rotulo} openDelay={400}>
      <IconButton
        aria-label={rotulo}
        size="xs"
        variant={ordem === '' ? 'ghost' : 'solid'}
        colorScheme={ordem === '' ? 'gray' : 'blue'}
        color={ordem === '' ? corInativo : undefined}
        ml="auto"
        icon={
          ordem === 'asc' ? (
            <ArrowUpNarrowWide size={14} />
          ) : ordem === 'desc' ? (
            <ArrowDownWideNarrow size={14} />
          ) : (
            <ArrowUpDown size={14} />
          )
        }
        onClick={() => setOrdem(proxima[ordem])}
      />
    </Tooltip>
  );
}

// Altura fixa das linhas de Baixados, Em Aberto, Saques, Depósitos e
// Transferências: como ficam em colunas lado a lado, descrição de tamanho
// variável desalinhava uma coluna da outra a cada lançamento.
const ALTURA_LINHA_FINANCEIRO = '68px';

// ─── sub-componente de linha do extrato ───
interface LinhaExtratoProps {
  tx: LancamentoExtrato;
  isActive: boolean;
  isFlashing: boolean;
  hasSugestao: boolean;
  onClick: () => void;
}

function LinhaExtrato({
  tx,
  isActive,
  isFlashing,
  hasSugestao,
  onClick,
}: LinhaExtratoProps) {
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const selecionadoLinha = useColorModeValue(
    'rgba(0, 130, 250, 0.15)',
    'rgba(0,50,200,0.1)',
  );
  return (
    <Flex
      as="button"
      w="100%"
      align="center"
      gridGap="3"
      px="4"
      py="3.5"
      textAlign="left"
      borderLeftWidth="4px"
      borderLeftColor={isActive ? 'blue.500' : 'transparent'}
      bg={isFlashing ? 'green.50' : isActive ? selecionadoLinha : undefined}
      _hover={{ bg: isActive ? undefined : subtleBg }}
      onClick={onClick}
    >
      <Box flex="1" minW="0">
        <Text
          fontSize="sm"
          fontWeight="semibold"
          whiteSpace="normal"
          wordBreak="normal"
        >
          {tx.descricao_exb}
        </Text>
        <Text fontSize="xs" color="gray.400" mt="0.5">
          {formatDataExtrato(tx.data_exb)}
          {tx.referencia_exb ? ` • ${tx.referencia_exb}` : ''}
        </Text>
      </Box>
      <Box flexShrink={0} textAlign="right" whiteSpace="nowrap">
        <Text
          fontSize="sm"
          fontWeight="bold"
          color={Number(tx.valor_exb) >= 0 ? 'green.600' : 'red.500'}
        >
          {formatValue(tx.valor_exb)}
        </Text>
        {hasSugestao && !isActive && (
          <Text fontSize="xs" color="green.500" fontWeight="medium" mt="0.5">
            Sugestão
          </Text>
        )}
      </Box>
    </Flex>
  );
}

// ─── sub-componente de linha financeira ───
function FinancialRow({
  tx,
  bankAmount,
  highlight,
  linking,
  onClick,
  selecionado,
  onSelecionar,
}: FinancialRowProps) {
  const sugestao = useColorModeValue(
    'rgba(0,200,50,0.2)',
    'rgba(0,200,50,0.1)',
  );
  // Mesmos tons de hover da lista de títulos em aberto: claro fixo clareava a
  // linha inteira no Dark Mode.
  const hover = useColorModeValue(
    'rgba(0, 130, 250, 0.10)',
    'rgba(120, 170, 255, 0.14)',
  );
  const hoverSugestao = useColorModeValue(
    'rgba(0,200,50,0.32)',
    'rgba(0,200,50,0.2)',
  );
  // bankAmount vem com sinal; valorbaixa_fin e sempre positivo.
  const diff = Math.abs(Number(tx.valorbaixa_fin) - Math.abs(bankAmount));
  return (
    // A caixa de seleção fica fora do botão: marcá-la junta o título ao lote,
    // clicar na linha continua conciliando só ele. Dois gestos, dois alvos.
    <Flex
      align="center"
      borderBottomWidth="1px"
      _last={{ borderBottomWidth: 0 }}
      bg={highlight ? sugestao : undefined}
    >
      <Box pl="4" flexShrink={0}>
        <OktonCheckbox
          name={`conciliacao-baixado-${tx.id_fin}`}
          label={null}
          size="md"
          aria-label="Selecionar título"
          isChecked={selecionado}
          onChange={onSelecionar}
        />
      </Box>
      <Flex
        as="button"
        flex="1"
        minW="0"
        h={ALTURA_LINHA_FINANCEIRO}
        overflow="hidden"
        align="center"
        gridGap="3"
        px="5"
        py="3.5"
        textAlign="left"
        _hover={{ bg: highlight ? hoverSugestao : hover }}
        disabled={linking}
        opacity={linking ? 0.7 : 1}
        onClick={onClick}
      >
        <Flex
          flexShrink={0}
          w="8"
          h="8"
          mr="4"
          borderRadius="full"
          align="center"
          justify="center"
          bg={highlight ? 'green.100' : 'gray.100'}
        >
          {linking ? (
            <Spinner size="xs" color="blue.500" />
          ) : highlight ? (
            <CheckCircle2 size={16} color="#059669" />
          ) : (
            <FileText size={16} color="#9ca3af" />
          )}
        </Flex>

        <Box flex="1" minW="0">
          <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
            {tx.nome_cli ?? tx.observacao_fin ?? 'Título'}
          </Text>
          <Text fontSize="xs" color="gray.500" mt="0.5" noOfLines={1}>
            Baixa {formatCustomDate(tx.baixa_fin, 'yyyy-MM-dd', 'dd/MM/yyyy')}
            {tx.vencimento_fin
              ? `${formatCustomDate(
                  tx.vencimento_fin.slice(0, 10),
                  'yyyy-MM-dd',
                  'dd/MM/yyyy',
                )}`
              : ''}
            {tx.centrocusto_fin ? ` • ${tx.centrocusto_fin}` : ''}
            {tx.documento_fin ? ` • ${tx.documento_fin}` : ''}
          </Text>
        </Box>

        <Box flexShrink={0} textAlign="right" whiteSpace="nowrap">
          <Text
            fontSize="sm"
            fontWeight="bold"
            color={tx.tipo_fin === TIPO_FIN_RECEBER ? 'green.600' : 'red.500'}
          >
            {formatValue(Number(tx.valorbaixa_fin))}
          </Text>
          {diff > 0.01 && (
            <Text fontSize="sm" color="orange.500" fontWeight="medium">
              Dif: {formatValue(diff)}
            </Text>
          )}
        </Box>
      </Flex>
    </Flex>
  );
}

// ─── componente principal ───
// Tipo de documento da Moeda de Cheque de Terceiro, como o IFLEX numera.
const TIPO_TITULO_CHEQUETERCEIRO = '14';

// A lista de cheques divide o espaço com o resto do formulário de cadastro,
// que é estreito: três por página cabem sem empurrar os botões para fora.
const CHEQUES_POR_PAGINA = 3;

const Conciliacao = () => {
  const history = useHistory();
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const bg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const sugestao2 = useColorModeValue(
    'rgba(0, 130, 35, 0.3)',
    'rgba(0, 255, 98, 0.1)',
  );
  // O mesmo verde que o FinancialRow usa na linha sugerida, aqui no nível da
  // página: Saques, Depósitos e Transferências também são sugeridos agora.
  const sugestaoLinha = useColorModeValue(
    'rgba(0,200,50,0.2)',
    'rgba(0,200,50,0.1)',
  );
  // Hover das linhas. Claro fixo (blue.50, green.100) clareava a linha inteira
  // no Dark Mode; a versão translúcida escurece ou clareia conforme o tema.
  const hoverLinha = useColorModeValue(
    'rgba(0, 130, 250, 0.10)',
    'rgba(120, 170, 255, 0.14)',
  );
  const hoverSugestao = useColorModeValue(
    'rgba(0,200,50,0.32)',
    'rgba(0,200,50,0.2)',
  );
  // Fundo da linha escolhida. Não se chama 'selecionado' porque o map dos
  // títulos em aberto declara uma variável com esse nome e sombreava o token.
  const selecionadoLinha = useColorModeValue(
    'rgba(0, 130, 250, 0.15)',
    'rgba(0,50,200,0.1)',
  );

  // Quem chega pelo Dashboard já vem com Conta, aba e período escolhidos:
  // é o recorte do número em que o usuário clicou.
  const { state: vindoDe } = useLocation<EstadoConciliacao | undefined>();

  // estados
  const [filtroData, setFiltroData] = useState<FiltroData>(
    vindoDe?.periodo ?? setaFiltroMensal(),
  );
  // Sem Conta vinda de fora, comeca vazia porque a lista de Contas ainda nao
  // chegou; o efeito abaixo escolhe a primeira assim que ela chegar.
  const [filterAccount, setFilterAccount] = useState(vindoDe?.conta ?? '');
  const [tab, setTab] = useState<'pending' | 'done'>(vindoDe?.tab ?? 'pending');
  const [activeBankId, setActiveBankId] = useState<string | null>(null);
  // Aba da coluna da direita: escolher um documento que já existe ou cadastrar
  // um novo a partir do lançamento.
  const [abaDireita, setAbaDireita] = useState<'consulta' | 'cadastro'>(
    'consulta',
  );
  const [abaConsulta, setAbaConsulta] = useState<AbaConsulta>('baixados');
  const [buscaConsulta, setBuscaConsulta] = useState('');
  const [buscaExtrato, setBuscaExtrato] = useState('');
  // Trocar de aba com um termo digitado deixaria a busca "grudada" numa aba
  // que não tem mais nada a ver com ela.
  const mudarAbaConsulta = (aba: AbaConsulta) => {
    setAbaConsulta(aba);
    setBuscaConsulta('');
  };
  const [tipoCadastro, setTipoCadastro] = useState<TipoCadastro>('lancamento');
  const [formGerar, setFormGerar] = useState({ ...GERAR_VAZIO });
  const [formTransferencia, setFormTransferencia] = useState({
    ...TRANSFERENCIA_VAZIA,
  });
  const [formDocumento, setFormDocumento] = useState({
    ...DOCUMENTO_VAZIO,
  });
  const colunaRef = useRef<HTMLDivElement>(null);
  const [alturaColuna, setAlturaColuna] = useState<number>();
  const [buscaCliente, setBuscaCliente] = useState('');
  const [erroCadastro, setErroCadastro] = useState('');
  const [abertoSelecionado, setAbertoSelecionado] = useState<string | null>(
    null,
  );
  // Títulos marcados para conciliar em lote. Guarda o valor junto porque o
  // título em aberto entra pelo saldo e o baixado pelo valor da baixa.
  const [selecao, setSelecao] = useState<
    { id: string; valor: number; aberto: boolean }[]
  >([]);
  const [ordemBaixados, setOrdemBaixados] = useState<OrdemValor>('');
  const [ordemAbertos, setOrdemAbertos] = useState<OrdemValor>('');
  const toast = useToast();
  const [confirmarAutoMatch, setConfirmarAutoMatch] = useState(false);

  const notificarErro = (title: string) =>
    toast({
      title,
      status: 'error',
      position: 'bottom-right',
      isClosable: true,
    });

  const notificarAviso = (title: string) =>
    toast({
      title,
      status: 'warning',
      position: 'bottom-right',
      isClosable: true,
    });

  const notificarSucesso = (title: string) =>
    toast({
      title,
      status: 'success',
      position: 'bottom-right',
      isClosable: true,
    });

  // O que falta ao título em aberto para virar baixa.
  const [formBaixa, setFormBaixa] = useState({
    moeda: '',
    caixa: '',
    observacao: '',
  });
  // Fechado por padrão: o cadastro pela conciliação quase sempre é só
  // Cliente, Referência e valor.
  const [mostrarClassificacoes, setMostrarClassificacoes] = useState(false);
  // Quantos campos da seção fechada já têm valor. Sem isto, fechar a seção
  // depois de preencher daria a impressão de que nada foi classificado.
  const classificacoesPreenchidas = [
    formGerar.direcionador,
    formGerar.departamento,
    formGerar.centrocusto,
    formGerar.observacao.trim(),
  ].filter(Boolean).length;
  const [flashId] = useState<string | null>(null);

  // dados
  const {
    data: contas = [],
    isLoading: isContasLoading,
    refetch: refetchContas,
  } = useContasBancarias();

  // Sempre com uma Conta escolhida. Também cobre a Conta que sumiu por fora
  // — excluída no IFLEX, ou filial trocada: sem isto o filtro ficaria
  // apontando para um código que não existe mais e a tela viria vazia sem
  // dizer por quê.
  useEffect(() => {
    if (contas.length === 0) return;
    if (contas.some(c => c.id_cts === filterAccount)) return;
    setFilterAccount(contas[0].id_cts);
    setActiveBankId(null);
  }, [contas, filterAccount]);
  // Só Conta do Sicoob, Bradesco ou Santander tem a integração de extrato via
  // API: o botão de consulta só aparece quando a Conta do filtro é de um deles.
  const contaFiltro = contas.find(c => c.id_cts === filterAccount);
  const bancoExtratoApi = bancoComExtratoApi(contaFiltro?.confcomp_ban);
  const comConta = filterAccount !== '';
  // A aba de cadastro rápido é a única que usa estas listas. Enquanto ela
  // está fechada, nem vão ao servidor.
  const abaCadastro = abaDireita === 'cadastro';
  const { data: moedasPagamento = [] } = useMoedasPagamento(abaCadastro);
  // Depositar Cheque de Terceiro é escolher papéis que já existem, e não
  // digitar um valor. A lista só é buscada quando a Moeda é de cheque.
  const moedaDepositada = moedasPagamento.find(
    m => m.id_moe === formDocumento.moeda,
  );
  const depositoDeCheque =
    tipoCadastro === 'deposito' &&
    moedaDepositada?.tipodocumento_tpt === TIPO_TITULO_CHEQUETERCEIRO;
  const { data: chequesDisponiveis = [] } =
    useChequesDisponiveis(depositoDeCheque);
  const [chequesEscolhidos, setChequesEscolhidos] = useState<string[]>([]);
  const [buscaCheque, setBuscaCheque] = useState('');
  const [paginaCheque, setPaginaCheque] = useState(0);

  // A busca varre o que está escrito na linha: número, emitente e banco.
  const chequesFiltrados = chequesDisponiveis.filter(c => {
    const termo = buscaCheque.trim().toLowerCase();
    if (termo === '') return true;
    return [c.documento_fin, c.nomecliente_fin, c.nome_cli, c.nome_ban]
      .filter(Boolean)
      .some(texto => String(texto).toLowerCase().includes(termo));
  });
  const totalPaginasCheque = Math.max(
    1,
    Math.ceil(chequesFiltrados.length / CHEQUES_POR_PAGINA),
  );
  // A página pode ficar além do fim depois de filtrar; recuar para a
  // última evita a lista aparecer vazia com resultados existindo.
  const paginaAtualCheque = Math.min(paginaCheque, totalPaginasCheque - 1);
  const chequesDaPagina = chequesFiltrados.slice(
    paginaAtualCheque * CHEQUES_POR_PAGINA,
    paginaAtualCheque * CHEQUES_POR_PAGINA + CHEQUES_POR_PAGINA,
  );
  const somaChequesEscolhidos = chequesDisponiveis
    .filter(c => chequesEscolhidos.includes(c.id_fin))
    .reduce((total, c) => total + Number(c.valor_fin), 0);

  const limpaCheques = () => {
    setChequesEscolhidos([]);
    setBuscaCheque('');
    setPaginaCheque(0);
  };

  const alternarCheque = (id: string) => {
    setChequesEscolhidos(atual =>
      atual.includes(id) ? atual.filter(c => c !== id) : [...atual, id],
    );
  };

  const {
    data: lancamentosExtrato = [],
    isLoading: isExtratoLoading,
    isError: isExtratoError,
    refetch: refetchExtrato,
  } = useExtratoBancario(
    filterAccount || undefined,
    'pending',
    '',
    '',
    comConta,
  );

  const {
    data: lancamentosInterno = [],
    isLoading: isInternoLoading,
    isError: isInternoError,
    refetch: refetchInterno,
  } = useLancamentosFinanceiros(
    filterAccount || undefined,
    'pending',
    filtroData[1],
    filtroData[2],
    'banco',
    comConta,
  );

  const {
    data: conciliados = [],
    isLoading: isConciliadosLoading,
    isError: isConciliadosError,
    refetch: refetchConciliados,
  } = useConciliacoes(
    filterAccount || undefined,
    filtroData[1],
    filtroData[2],
    comConta,
  );

  // Quem chega pelo card de Divergências quer ver só elas. Sem nenhuma no
  // recorte, a lista cheia: tela vazia pareceria que os Conciliados sumiram.
  const [somenteDivergentes, setSomenteDivergentes] = useState(
    !!vindoDe?.divergentes,
  );
  const conciliadosDivergentes = useMemo(
    () => conciliados.filter(c => c.divergente_cbc === '1'),
    [conciliados],
  );
  const filtrandoDivergentes =
    somenteDivergentes && conciliadosDivergentes.length > 0;

  const {
    ordenacao: ordenacaoConciliados,
    alternarOrdenacao: alternarConciliados,
    itens: conciliadosVisiveis,
    pagina: paginaConciliados,
    setPagina: setPaginaConciliados,
    tamanhoPagina: tamanhoPaginaConciliados,
    setTamanhoPagina: setTamanhoPaginaConciliados,
  } = useGrid(
    filtrandoDivergentes ? conciliadosDivergentes : conciliados,
    COLUNAS_CONCILIADOS,
  );

  const { data: lancamentosAbertos = [] } = useLancamentosAbertos(
    filtroData[1],
    filtroData[2],
  );

  const linkMutation = useLinkConciliados();
  const baixaMutation = useBaixarEConciliar();
  const variosMutation = useVincularVarios();
  const unmatchMutation = useUnlinkConciliados();
  const autoMatchMutation = useAutoMatch();
  const gerarMutation = useGerarFinanceiro();
  const transferenciaMutation = useCadastrarTransferencia();
  const saqueMutation = useCadastrarSaque();
  const depositoMutation = useCadastrarDeposito();
  const linkTransferencia = useLinkTransferencia();
  const linkDocumento = useLinkDocumento();
  // 'pending' porque o que interessa aqui e o que falta conciliar.
  const { data: saques = [], refetch: refetchSaques } = useSaques(
    filterAccount || undefined,
    filtroData[1],
    filtroData[2],
    'pending',
    comConta,
  );
  const { data: depositos = [], refetch: refetchDepositos } = useDepositos(
    filterAccount || undefined,
    filtroData[1],
    filtroData[2],
    'pending',
    comConta,
  );
  const { data: transferencias = [], refetch: refetchTransferencias } =
    useTransferencias(
      filterAccount || undefined,
      'pending',
      filtroData[1],
      filtroData[2],
      comConta,
    );

  // Sugestões: mesmo valor em módulo, mesmo sentido e dentro de 5 dias.
  // Espelha o critério do auto-match do back — valorbaixa_fin é sempre
  // positivo e quem dá o sentido é tipo_fin, então comparar sinal direto
  // casaria um recebimento com um pagamento de mesmo valor. Devolve a aba
  // onde a sugestão mora, não só se existe: é o que deixa selecionar um
  // lançamento do extrato já abrir na aba certa, em vez de sempre abrir em
  // Baixados e o usuário ter que procurar.
  const abaDaSugestao = useCallback(
    (tx: LancamentoExtrato): AbaConsulta | null => {
      const valor = Number(tx.valor_exb);
      const saida = valor < 0;
      const tipo = saida ? TIPO_FIN_PAGAR : TIPO_FIN_RECEBER;
      const bate = (v: number | string, d: string | null) =>
        combinaComExtrato(v, d, valor, tx.data_exb);

      if (
        lancamentosInterno.some(
          t => t.tipo_fin === tipo && bate(t.valorbaixa_fin, t.baixa_fin),
        )
      ) {
        return 'baixados';
      }
      if (
        lancamentosAbertos.some(
          t => t.tipo_fin === tipo && bate(t.saldo_fin, t.vencimento_fin),
        )
      ) {
        return 'aberto';
      }
      // Saque é saída da Conta, Depósito é entrada: o sentido do extrato
      // decide qual dos dois pode explicá-lo.
      const documentos = saida
        ? saques
            .filter(s => s.id_cts === tx.conta_exb)
            .map(s => ({ valor: s.valor_saq, data: s.data_saq }))
        : depositos
            .filter(d => d.id_cts === tx.conta_exb)
            .map(d => ({ valor: d.valor_dep, data: d.data_dep }));
      if (documentos.some(d => bate(d.valor, d.data))) {
        return 'saquesDepositos';
      }
      const temTransferencia = transferencias.some(tba => {
        const ponta = pontaDaConta(tba, tx.conta_exb);
        if (!ponta || ponta.conciliada || ponta.saida !== saida) return false;
        return bate(tba.valor_tba, tba.data_tba);
      });
      return temTransferencia ? 'transferencias' : null;
    },
    [lancamentosInterno, lancamentosAbertos, saques, depositos, transferencias],
  );
  const temSugestao = useCallback(
    (tx: LancamentoExtrato) => abaDaSugestao(tx) !== null,
    [abaDaSugestao],
  );
  // Agrupa quem tem sugestão primeiro — é o que a coluna do Extrato usa pra
  // separar visualmente os dois grupos, e também o que decide qual item a
  // auto-seleção pega: sem isso, a tela sempre abria no primeiro da lista
  // que veio do banco, sugestão ou não.
  const extratoOrdenado = useMemo(
    () =>
      [...lancamentosExtrato].sort(
        (a, b) =>
          Number(abaDaSugestao(b) !== null) - Number(abaDaSugestao(a) !== null),
      ),
    [lancamentosExtrato, abaDaSugestao],
  );

  const { data: planosDFC = [] } = usePlanosDFC(undefined, abaCadastro);
  const { data: parametros } = useParametros();
  // Com Controla DRE ligado o IFLEX exige Direcionador no titulo e Plano de
  // Contas no Direcionador novo e na Transferencia.
  const controlaDRE = usaControlaDRE(parametros);
  const { data: centrosCusto = [] } = useCentrosCusto(abaCadastro);
  const { data: departamentos = [] } = useDepartamentos(abaCadastro);
  const { data: direcionadores = [] } = useDirecionadores(abaCadastro);
  const { data: clientes = [], isFetching: isClientesFetching } =
    useClientes(buscaCliente);
  const createCentroCusto = useCreateCentroCusto();
  const createDepartamento = useCreateDepartamento();
  const createDirecionador = useCreateDirecionador();
  const createCliente = useCreateCliente();

  // Endereço vindo da consulta de CNPJ do cadastro rápido de Cliente: não
  // aparece na tela, só viaja junto no cadastro do Cliente quando ele é
  // salvo (é o que "cadastrando endereço automaticamente" pede).
  const [enderecoNovoCliente, setEnderecoNovoCliente] = useState<
    EnderecoConsultaCnpj | undefined
  >();
  // Uma vez que o usuário digite algo no Nome, a consulta de CNPJ nunca mais
  // sobrescreve — checar só "campo vazio" não bastava: se o usuário apagasse
  // o nome sugerido pra escrever outro do zero, o instante em que o campo
  // ficava vazio de novo era reaproveitado pela consulta pra preencher com o
  // nome da Receita outra vez.
  const nomeClienteDigitadoRef = useRef(false);
  const cadastroLancamentoRef = useRef(false);
  const { data: cnpjConsultadoGerar } = useConsultaCnpj(
    formGerar.clientenovotipo === TIPO_CLIENTE_PJ
      ? formGerar.clientenovodocumento
      : '',
  );
  useEffect(() => {
    if (!cnpjConsultadoGerar?.encontrado) return;
    if (!nomeClienteDigitadoRef.current) {
      setFormGerar(f => ({
        ...f,
        clientenovonome: cnpjConsultadoGerar.nome ?? f.clientenovonome,
      }));
    }
    setEnderecoNovoCliente(cnpjConsultadoGerar.endereco);
  }, [cnpjConsultadoGerar]);

  // Documento do "Novo Cliente" já pertence a outro Cliente cadastrado: sem
  // este aviso, o Cliente novo nem chegava a ser criado — o cadastro rápido
  // vinculava o lançamento ao Cliente já existente por baixo dos panos, sem
  // avisar que o nome digitado não era o que ficou gravado.
  const { data: clienteComDocumento } = useClientePorDocumento(
    formGerar.cliente === CLIENTE_NOVO ? formGerar.clientenovodocumento : '',
  );

  // Entre a lista de Contas chegar e o filtro receber a primeira, as listas
  // ainda estão paradas: sem contar esse intervalo, a tela piscaria vazia.
  const aguardandoConta = contas.length > 0 && !comConta;
  const isLoading =
    isContasLoading || aguardandoConta || isExtratoLoading || isInternoLoading;
  // Sem Conta cadastrada não há extrato nem lançamento para casar: a tela
  // inteira depende de uma, e o filtro agora obriga a escolher.
  const semConta = !isContasLoading && contas.length === 0;
  const isError = isExtratoError || isInternoError;

  const recarregar = () => {
    refetchContas();
    refetchExtrato();
    refetchInterno();
    refetchConciliados();
    refetchTransferencias();
    refetchSaques();
    refetchDepositos();
  };
  const autoConciliar = autoMatchMutation.isLoading;
  // enquanto o vínculo está sendo salvo, exibe o spinner na linha que foi clicada
  const linkingId = linkMutation.isLoading
    ? linkMutation.variables?.financeiro ?? null
    : null;

  // seleciona o extrato ativo
  const activeLancamentoExtrato = useMemo(
    () => lancamentosExtrato.find(t => t.id_exb === activeBankId) ?? null,
    [lancamentosExtrato, activeBankId],
  );

  // As Moedas da Conta do lançamento selecionado: é entre elas que se diz por
  // onde o dinheiro entrou.
  const { data: moedasDaConta = [] } = useVinculosDaConta(
    activeLancamentoExtrato?.conta_exb,
  );

  useLayoutEffect(() => {
    // So mede com lancamento selecionado: sem selecao a direita mostra o aviso
    // curto, e a esquerda encolheria junto.
    const alturaAtual = colunaRef.current?.offsetHeight;
    if (
      activeLancamentoExtrato &&
      alturaAtual &&
      alturaAtual !== alturaColuna
    ) {
      setAlturaColuna(alturaAtual);
    }
  }, [
    alturaColuna,
    activeLancamentoExtrato,
    abaDireita,
    // O tipo troca os campos do formulário, e com eles a altura da coluna.
    tipoCadastro,
    erroCadastro,
    formGerar.centrocusto,
    formGerar.departamento,
    formGerar.direcionador,
  ]);

  // Trocar de lançamento zera o formulário: os campos descrevem o lançamento
  // selecionado, e reaproveitá-los geraria um título com dados do anterior.
  // Quem decide a aba de Consulta é quem escolhe o lançamento (clique na
  // lista ou auto-seleção): fazer isso aqui, num efeito disparado por
  // activeBankId, correria atrás do próprio estado que acabou de setar.
  useEffect(() => {
    setFormGerar({ ...GERAR_VAZIO });
    setEnderecoNovoCliente(undefined);
    nomeClienteDigitadoRef.current = false;
    setFormTransferencia({ ...TRANSFERENCIA_VAZIA });
    setFormDocumento({ ...DOCUMENTO_VAZIO });
    setBuscaCliente('');
    setErroCadastro('');
    setBuscaConsulta('');
  }, [activeBankId]);

  // Auto-seleciona o primeiro lançamento pendente — com o extrato agrupado,
  // esse primeiro já é um com sugestão quando existir, e a aba abre nela.
  useEffect(() => {
    if (!activeBankId && extratoOrdenado.length > 0) {
      const primeiro = extratoOrdenado[0];
      setActiveBankId(primeiro.id_exb);
      setAbaConsulta(abaDaSugestao(primeiro) ?? 'baixados');
    }
  }, [extratoOrdenado, activeBankId, abaDaSugestao]);

  // Sugestões: mesmo valor em módulo, mesmo sentido e dentro de 5 dias.
  // Espelha o critério do auto-match do back — valorbaixa_fin é sempre
  // positivo e quem dá o sentido é tipo_fin, então comparar sinal direto
  // casaria um recebimento com um pagamento de mesmo valor.
  // O sentido do extrato decide o que pode aparecer: entrada (crédito) só
  // casa com título a Receber, saída (débito) só com título a Pagar. Um
  // pagamento nunca explica um recebimento, então mostrar os dois lados só
  // daria trabalho de descartar - e risco de conciliar o errado.
  const tipoEsperado = activeLancamentoExtrato
    ? Number(activeLancamentoExtrato.valor_exb) < 0
      ? TIPO_FIN_PAGAR
      : TIPO_FIN_RECEBER
    : null;

  // Tudo que nasce desta tela tem a data do lançamento do extrato, não a de
  // hoje: é nela que o Caixa precisa estar aberto.
  const dataDoExtrato = activeLancamentoExtrato
    ? activeLancamentoExtrato.data_exb.slice(0, 10)
    : '';

  const baixadosCompativeis = useMemo(
    () =>
      tipoEsperado === null
        ? []
        : lancamentosInterno.filter(t => t.tipo_fin === tipoEsperado),
    [lancamentosInterno, tipoEsperado],
  );

  const suggestions = useMemo(() => {
    if (!activeLancamentoExtrato) return [];
    const valorExtrato = Number(activeLancamentoExtrato.valor_exb);
    return ordenaPorValor(
      baixadosCompativeis.filter(
        t =>
          Math.abs(Number(t.valorbaixa_fin) - Math.abs(valorExtrato)) < 0.01 &&
          // Só as datas: baixa e extrato trazem hora, e a janela é em dias.
          daysDiff(
            t.baixa_fin.slice(0, 10),
            activeLancamentoExtrato.data_exb.slice(0, 10),
          ) <= 5,
      ),
      t => Number(t.valorbaixa_fin),
      ordemBaixados,
    );
  }, [baixadosCompativeis, activeLancamentoExtrato, ordemBaixados]);

  // Uma Transferencia so explica o lancamento quando a Conta dele e uma das
  // pontas, aquela ponta ainda esta por conciliar e o sentido bate: saida da
  // Conta casa com valor negativo no extrato, entrada com positivo.
  // A que combina em valor e data vem marcada e sobe para o topo: e a mesma
  // sugestao que os titulos baixados ja tinham.
  const transferenciasDisponiveis = useMemo(() => {
    if (!activeLancamentoExtrato) return [];
    const valorExtrato = Number(activeLancamentoExtrato.valor_exb);
    const dataExtrato = activeLancamentoExtrato.data_exb;
    const lista = transferencias
      .filter(tba => {
        const ponta = pontaDaConta(tba, activeLancamentoExtrato.conta_exb);
        if (!ponta || ponta.conciliada) return false;
        return ponta.saida === valorExtrato < 0;
      })
      .map(tba => ({
        ...tba,
        sugerida: combinaComExtrato(
          tba.valor_tba,
          tba.data_tba,
          valorExtrato,
          dataExtrato,
        ),
      }))
      .sort((a, b) => Number(b.sugerida) - Number(a.sugerida));
    return ordemBaixados === ''
      ? lista
      : ordenaPorValor(lista, t => Number(t.valor_tba), ordemBaixados);
  }, [transferencias, activeLancamentoExtrato, ordemBaixados]);

  // Saque e saida da Conta, Deposito e entrada: o sentido do extrato tem que
  // bater, e a Conta do documento tem que ser a do lancamento.
  const documentosDisponiveis = useMemo(() => {
    if (!activeLancamentoExtrato) return [];
    const valorExtrato = Number(activeLancamentoExtrato.valor_exb);
    const dataExtrato = activeLancamentoExtrato.data_exb;
    const saida = valorExtrato < 0;
    const conta = activeLancamentoExtrato.conta_exb;
    const daConta = saida
      ? saques
          .filter(s => s.id_cts === conta)
          .map(s => ({
            tipo: 'saque' as const,
            id: s.id_saq,
            data: s.data_saq,
            valor: s.valor_saq,
            descricao: s.observacao_saq ?? 'Saque',
          }))
      : depositos
          .filter(d => d.id_cts === conta)
          .map(d => ({
            tipo: 'deposito' as const,
            id: d.id_dep,
            data: d.data_dep,
            valor: d.valor_dep,
            descricao: d.observacao_dep ?? d.envelope_dep ?? 'Depósito',
          }));
    const lista = daConta
      .map(doc => ({
        ...doc,
        sugerido: combinaComExtrato(
          doc.valor,
          doc.data,
          valorExtrato,
          dataExtrato,
        ),
      }))
      .sort((a, b) => Number(b.sugerido) - Number(a.sugerido));
    return ordemBaixados === ''
      ? lista
      : ordenaPorValor(lista, d => Number(d.valor), ordemBaixados);
  }, [saques, depositos, activeLancamentoExtrato, ordemBaixados]);

  const others = useMemo(
    () =>
      ordenaPorValor(
        baixadosCompativeis.filter(
          t => !suggestions.find(s => s.id_fin === t.id_fin),
        ),
        t => Number(t.valorbaixa_fin),
        ordemBaixados,
      ),
    [baixadosCompativeis, suggestions, ordemBaixados],
  );

  // Cada aba da Consulta (Saques, Depósitos, Transferências) só mostra o que
  // combina com o sentido do extrato selecionado - documentosDisponiveis já
  // nasce só de um tipo por vez, então filtrar aqui é só separar a aba certa.
  const saquesDisponiveis = useMemo(
    () => documentosDisponiveis.filter(d => d.tipo === 'saque'),
    [documentosDisponiveis],
  );
  const depositosDisponiveis = useMemo(
    () => documentosDisponiveis.filter(d => d.tipo === 'deposito'),
    [documentosDisponiveis],
  );

  // Paginação das listas que crescem sem limite: o extrato de uma conta
  // recém-importada e os lançamentos financeiros do período podem chegar a
  // centenas de linhas, e renderizar tudo de uma vez travava a tela. Sem
  // colunas de ordenação porque as linhas aqui são botões custom, não uma
  // <table> — o hook entra pela paginação e pela busca de cada aba.
  const extratoGrid = useGrid(
    extratoOrdenado,
    COLUNAS_BUSCA_EXTRATO,
    buscaExtrato,
  );
  // A página já vem com quem tem sugestão primeiro (extratoOrdenado); aqui é
  // só separar em dois grupos pra desenhar o cabeçalho entre eles, do mesmo
  // jeito que Baixados separa Sugestão automática de Outros lançamentos.
  const extratoComSugestao = useMemo(
    () => extratoGrid.itens.filter(tx => temSugestao(tx)),
    [extratoGrid.itens, temSugestao],
  );
  const extratoSemSugestao = useMemo(
    () => extratoGrid.itens.filter(tx => !temSugestao(tx)),
    [extratoGrid.itens, temSugestao],
  );
  const outrosGrid = useGrid(others, COLUNAS_BUSCA_FINANCEIRO, buscaConsulta);
  const saquesGrid = useGrid(
    saquesDisponiveis,
    COLUNAS_BUSCA_DOCUMENTO,
    buscaConsulta,
  );
  const depositosGrid = useGrid(
    depositosDisponiveis,
    COLUNAS_BUSCA_DOCUMENTO,
    buscaConsulta,
  );
  const transferenciasGrid = useGrid(
    transferenciasDisponiveis,
    COLUNAS_BUSCA_TRANSFERENCIA,
    buscaConsulta,
  );
  // Sugestões não paginam - são poucas por natureza -, mas ainda assim
  // precisam sumir da lista quando a busca da aba não acha nada nelas.
  const suggestionsFiltradas = useMemo(
    () => filtrarPorBusca(suggestions, COLUNAS_BUSCA_FINANCEIRO, buscaConsulta),
    [suggestions, buscaConsulta],
  );

  // O título em aberto ainda não tem sentido de baixa, mas já nasce a Pagar
  // ou a Receber: vale o mesmo filtro dos baixados. O que combina em saldo e
  // vencimento vem marcado e sobe para o topo.
  const abertosDoPeriodo = useMemo(() => {
    if (tipoEsperado === null || !activeLancamentoExtrato) return [];
    const valorExtrato = Number(activeLancamentoExtrato.valor_exb);
    const dataExtrato = activeLancamentoExtrato.data_exb;
    const lista = lancamentosAbertos
      .filter(t => t.tipo_fin === tipoEsperado)
      .map(t => ({
        ...t,
        sugerido: combinaComExtrato(
          t.saldo_fin,
          t.vencimento_fin,
          valorExtrato,
          dataExtrato,
        ),
      }))
      .sort((a, b) => Number(b.sugerido) - Number(a.sugerido));
    return ordemAbertos === ''
      ? lista
      : ordenaPorValor(lista, t => Number(t.saldo_fin), ordemAbertos);
  }, [lancamentosAbertos, tipoEsperado, activeLancamentoExtrato, ordemAbertos]);
  const abertosGrid = useGrid(
    abertosDoPeriodo,
    COLUNAS_BUSCA_ABERTOS,
    buscaConsulta,
  );

  const alternaSelecao = (id: string, valor: number, aberto: boolean) =>
    setSelecao(atual =>
      atual.find(s => s.id === id)
        ? atual.filter(s => s.id !== id)
        : [...atual, { id, valor, aberto }],
    );

  const totalSelecionado = selecao.reduce((s, item) => s + item.valor, 0);
  // O extrato vem com sinal; os títulos entram sempre positivos.
  const valorExtrato = activeLancamentoExtrato
    ? Math.abs(Number(activeLancamentoExtrato.valor_exb))
    : 0;
  const sobraSelecao = Number((valorExtrato - totalSelecionado).toFixed(2));

  const selecaoTemAberto = selecao.some(s => s.aberto);

  // Conciliar títulos em aberto os BAIXA, e a baixa grava Moeda e observação.
  // Isso é decisão de quem concilia, não padrão silencioso — e vai numa tela
  // própria, que é o padrão do sistema para o que grava. Só baixados vão
  // direto, porque não há o que decidir.
  const confirmarSelecao = () => {
    if (!activeBankId || !activeLancamentoExtrato || selecao.length === 0) {
      return;
    }
    if (!selecaoTemAberto) {
      variosMutation.mutate(
        {
          extrato: activeBankId,
          financeiros: selecao.map(s => s.id),
          moeda: '',
          caixa: '',
          observacao: '',
          // O extrato fecha: os títulos escolhidos são o que explica aquele
          // lançamento, batendo o valor ou não.
          sobra: 'conciliado',
        },
        {
          onSuccess: () => {
            setSelecao([]);
            setActiveBankId(null);
          },
          onError: erro => {
            notificarErro(
              erro instanceof Error
                ? erro.message
                : 'Erro ao conciliar os títulos.',
            );
          },
        },
      );
      return;
    }
    history.push('/conciliacao/baixar', {
      extrato: activeBankId,
      conta: activeLancamentoExtrato.conta_exb,
      financeiros: selecao.map(s => s.id),
      valorExtrato,
      totalSelecionado,
      dataExtrato: activeLancamentoExtrato.data_exb,
      descricaoExtrato: activeLancamentoExtrato.descricao_exb,
    });
  };

  const baixarEConciliar = async (financeiro: string) => {
    if (!activeBankId) return;
    try {
      await baixaMutation.mutateAsync({
        extrato: activeBankId,
        financeiro,
        moeda: formBaixa.moeda,
        caixa: formBaixa.caixa,
        observacao: formBaixa.observacao,
      });
      setAbertoSelecionado(null);
      setFormBaixa({ moeda: '', caixa: '', observacao: '' });
      setActiveBankId(null);
    } catch (err) {
      notificarErro(
        err instanceof Error
          ? err.message
          : 'Erro ao baixar e conciliar o título.',
      );
    }
  };

  // ─── handlers ───
  const handleLink = (idFinanceiro: string) => {
    if (!activeBankId) return;
    linkMutation.mutate(
      {
        extrato: activeBankId,
        financeiro: idFinanceiro,
      },
      {
        onSuccess: () => setActiveBankId(null),
      },
    );
  };

  const handleLinkTransferencia = (idTransferencia: string) => {
    if (!activeBankId) return;
    linkTransferencia.mutate(
      { extrato: activeBankId, transferencia: idTransferencia },
      {
        onSuccess: () => setActiveBankId(null),
        onError: erro => {
          notificarErro(
            erro instanceof Error
              ? erro.message
              : 'Erro ao conciliar com a Transferência.',
          );
        },
      },
    );
  };

  const handleLinkDocumento = (tipo: 'saque' | 'deposito', id: string) => {
    if (!activeBankId) return;
    linkDocumento.mutate(
      { extrato: activeBankId, tipo, documento: id },
      {
        onSuccess: () => setActiveBankId(null),
        onError: erro => {
          notificarErro(
            erro instanceof Error ? erro.message : 'Erro ao conciliar.',
          );
        },
      },
    );
  };

  const handleUnmatch = (match: Conciliado) => {
    unmatchMutation.mutate(match.id_cbc);
  };

  const handleAutoMatch = () => {
    const conta = contas.find(c => c.id_cts === filterAccount);
    if (!conta) {
      notificarAviso(
        'Selecione uma Conta Bancária antes de conciliar automaticamente. A conciliação compara o extrato com os títulos daquela conta.',
      );
      return;
    }
    setConfirmarAutoMatch(true);
  };

  const executarAutoMatch = () => {
    autoMatchMutation.mutate(
      { conta: filterAccount, dataIni: filtroData[1], dataFim: filtroData[2] },
      {
        onSuccess: resultado => {
          setActiveBankId(null);
          setConfirmarAutoMatch(false);
          notificarSucesso(
            resultado.casados > 0
              ? `${resultado.casados} de ${resultado.extrato} lançamentos do extrato foram conciliados.`
              : `Nenhum par encontrado entre os ${resultado.extrato} lançamentos do extrato e os ${resultado.titulos} documentos pendentes.`,
          );
        },
        onError: erro => {
          notificarErro(
            erro instanceof Error
              ? erro.message
              : 'Erro ao conciliar automaticamente.',
          );
        },
      },
    );
  };

  // Qualquer um dos quatro cadastros ocupa o mesmo botão, então o spinner
  // olha as quatro mutations.
  const salvandoCadastro =
    gerarMutation.isLoading ||
    transferenciaMutation.isLoading ||
    saqueMutation.isLoading ||
    depositoMutation.isLoading;

  // Depois de gravar, o lançamento saiu de pendente: limpar a seleção e voltar
  // para Consulta evita o formulário apontando para um lançamento que já não
  // está mais na lista.
  const concluiCadastro = () => {
    setFormGerar({ ...GERAR_VAZIO });
    setEnderecoNovoCliente(undefined);
    nomeClienteDigitadoRef.current = false;
    setFormTransferencia({ ...TRANSFERENCIA_VAZIA });
    setFormDocumento({ ...DOCUMENTO_VAZIO });
    setBuscaCliente('');
    setActiveBankId(null);
    setAbaDireita('consulta');
    setTipoCadastro('lancamento');
    setMostrarClassificacoes(false);
    setSelecao([]);
    limpaCheques();
  };

  const cadastrarTransferencia = async () => {
    if (!activeBankId) return;
    if (!formTransferencia.documento.trim()) {
      setErroCadastro('Informe a Referência.');
      return;
    }
    if (!formTransferencia.origem || !formTransferencia.destino) {
      setErroCadastro('Informe a Conta de Origem e a Conta de Destino.');
      return;
    }
    if (formTransferencia.origem === formTransferencia.destino) {
      setErroCadastro(
        'A Conta de Origem deve ser diferente da Conta de Destino.',
      );
      return;
    }
    if (controlaDRE && !formTransferencia.planocontas) {
      setErroCadastro('Informe o Plano de Contas (DFC).');
      return;
    }
    setErroCadastro('');
    try {
      await transferenciaMutation.mutateAsync({
        extrato: activeBankId,
        documento: formTransferencia.documento.trim(),
        origem: formTransferencia.origem,
        destino: formTransferencia.destino,
        planocontas: formTransferencia.planocontas,
        observacao: formTransferencia.observacao.trim(),
      });
      concluiCadastro();
    } catch (erro) {
      setErroCadastro(
        erro instanceof Error
          ? erro.message
          : 'Erro ao cadastrar a Transferência.',
      );
    }
  };

  // Saque explica saída da Conta, Depósito explica entrada. O back recusa a
  // combinação errada; a tela avisa antes para o usuário não perder o
  // preenchimento.
  const cadastrarDocumento = async () => {
    if (!activeBankId || !activeLancamentoExtrato) return;
    const saida = Number(activeLancamentoExtrato.valor_exb) < 0;
    if (tipoCadastro === 'saque' && !saida) {
      setErroCadastro('Este lançamento é uma entrada: cadastre um Depósito.');
      return;
    }
    if (tipoCadastro === 'deposito' && saida) {
      setErroCadastro('Este lançamento é uma saída: cadastre um Saque.');
      return;
    }
    if (tipoCadastro === 'deposito' && !formDocumento.moeda) {
      setErroCadastro('Informe a forma de pagamento depositada.');
      return;
    }
    if (depositoDeCheque && chequesEscolhidos.length === 0) {
      setErroCadastro('Escolha ao menos um cheque para depositar.');
      return;
    }
    // O Depósito nasce conciliado com o lançamento: a soma dos cheques tem
    // que fechar com ele. O servidor confere de novo, relendo os valores.
    if (
      depositoDeCheque &&
      Math.abs(
        somaChequesEscolhidos -
          Math.abs(Number(activeLancamentoExtrato?.valor_exb ?? 0)),
      ) >= 0.005
    ) {
      setErroCadastro(
        'A soma dos cheques precisa fechar com o valor do lançamento do extrato.',
      );
      return;
    }
    if (controlaDRE && !formDocumento.planocontas) {
      setErroCadastro('Informe o Plano de Contas (DFC).');
      return;
    }
    setErroCadastro('');
    const comum = {
      extrato: activeBankId,
      planocontas: formDocumento.planocontas,
      observacao: formDocumento.observacao.trim(),
      caixa: formDocumento.caixa,
    };
    try {
      if (tipoCadastro === 'saque') {
        await saqueMutation.mutateAsync(comum);
      } else {
        await depositoMutation.mutateAsync({
          ...comum,
          moeda: formDocumento.moeda,
          envelope: formDocumento.envelope.trim(),
          cheques: depositoDeCheque ? JSON.stringify(chequesEscolhidos) : '',
        });
      }
      concluiCadastro();
    } catch (erro) {
      setErroCadastro(
        erro instanceof Error
          ? erro.message
          : `Erro ao cadastrar o ${
              tipoCadastro === 'saque' ? 'Saque' : 'Depósito'
            }.`,
      );
    }
  };

  const cadastrarLancamento = async () => {
    // Trava por ref, não só pelo estado das mutations: salvandoCadastro só
    // fica true a partir do createCliente/gerarMutation, e o duplo clique
    // pode disparar os dois handlers antes disso - foi o que deixava
    // cadastrar um Cliente (e até o lançamento) em dobro.
    if (cadastroLancamentoRef.current) return;
    if (!activeBankId) return;
    if (!formGerar.cliente) {
      setErroCadastro('Informe o Cliente.');
      return;
    }
    if (!formGerar.documento.trim()) {
      setErroCadastro('Informe a Referência.');
      return;
    }
    if (formGerar.cliente === CLIENTE_NOVO) {
      if (!formGerar.clientenovonome.trim()) {
        setErroCadastro('Informe o nome do novo Cliente.');
        return;
      }
      const documentoNovoCliente = soDigitos(formGerar.clientenovodocumento);
      if (
        formGerar.clientenovotipo === TIPO_CLIENTE_PJ &&
        documentoNovoCliente.length !== 14
      ) {
        setErroCadastro('Informe um CNPJ válido para o novo Cliente.');
        return;
      }
      if (
        formGerar.clientenovotipo === TIPO_CLIENTE_PF &&
        documentoNovoCliente !== '' &&
        documentoNovoCliente.length !== 11
      ) {
        setErroCadastro('Informe um CPF válido para o novo Cliente.');
        return;
      }
      if (clienteComDocumento?.existe && clienteComDocumento.cliente) {
        setErroCadastro(
          `Este CPF/CNPJ já pertence ao Cliente "${clienteComDocumento.cliente.nome_cli}" (código ${clienteComDocumento.cliente.id_cli}). Busque por ele no campo Cliente.`,
        );
        return;
      }
    }
    // Os campos abaixo ficam na seção retrátil. Se o erro apontasse para um
    // campo escondido, o usuário leria a mensagem sem enxergar o campo,
    // então a seção abre junto com o erro.
    const exigeClassificacao = (mensagem: string) => {
      setErroCadastro(mensagem);
      setMostrarClassificacoes(true);
    };
    if (
      formGerar.centrocusto === CENTRO_CUSTO_NOVO &&
      !formGerar.centrocustonovo.trim()
    ) {
      exigeClassificacao('Informe o nome do novo Centro de Custo.');
      return;
    }
    if (
      formGerar.departamento === DEPARTAMENTO_NOVO &&
      !formGerar.departamentonovo.trim()
    ) {
      exigeClassificacao('Informe o nome do novo Departamento.');
      return;
    }
    if (
      formGerar.direcionador === DIRECIONADOR_NOVO &&
      !formGerar.direcionadornovo.trim()
    ) {
      exigeClassificacao('Informe o nome do novo Direcionador.');
      return;
    }
    if (controlaDRE && !formGerar.direcionador) {
      exigeClassificacao('Informe o Direcionador.');
      return;
    }
    if (
      controlaDRE &&
      formGerar.direcionador === DIRECIONADOR_NOVO &&
      !formGerar.direcionadorplano
    ) {
      exigeClassificacao('Informe o Plano de Contas (DFC) do Direcionador.');
      return;
    }
    setErroCadastro('');
    cadastroLancamentoRef.current = true;
    try {
      let { centrocusto, departamento, direcionador, cliente } = formGerar;
      if (cliente === CLIENTE_NOVO) {
        const criado = await createCliente.mutateAsync({
          tipo: formGerar.clientenovotipo,
          nome: formGerar.clientenovonome.trim(),
          documento: soDigitos(formGerar.clientenovodocumento),
          ie: formGerar.clientenovoie.trim(),
          endereco: enderecoNovoCliente,
        });
        cliente = String(criado.id_cli);
      }
      if (centrocusto === CENTRO_CUSTO_NOVO) {
        const criado = await createCentroCusto.mutateAsync(
          formGerar.centrocustonovo.trim(),
        );
        centrocusto = String(criado.id_cdc);
      }
      if (departamento === DEPARTAMENTO_NOVO) {
        const criado = await createDepartamento.mutateAsync(
          formGerar.departamentonovo.trim(),
        );
        departamento = String(criado.id_dpt);
      }
      if (direcionador === DIRECIONADOR_NOVO) {
        const criado = await createDirecionador.mutateAsync({
          nome: formGerar.direcionadornovo.trim(),
          planocontas: formGerar.direcionadorplano,
        });
        direcionador = String(criado.id_ori);
      }
      await gerarMutation.mutateAsync({
        extrato: activeBankId,
        cliente,
        documento: formGerar.documento.trim(),
        observacao: formGerar.observacao.trim(),
        centrocusto,
        departamento,
        direcionador,
        caixa: formGerar.caixa,
        moeda: formGerar.moeda,
      });
      concluiCadastro();
    } catch (erro) {
      setErroCadastro(
        erro instanceof Error ? erro.message : 'Erro ao gerar o título.',
      );
    } finally {
      cadastroLancamentoRef.current = false;
    }
  };

  const salvarCadastro = () => {
    if (tipoCadastro === 'transferencia') {
      cadastrarTransferencia();
      return;
    }
    if (tipoCadastro === 'lancamento') {
      cadastrarLancamento();
      return;
    }
    cadastrarDocumento();
  };

  return (
    <BankAppPage>
      {/* ─── Header ─── */}
      <OktonPageHeader
        eyebrow="GERAL"
        title="Conciliação Bancária"
        subtitle="Selecione um lançamento do banco e clique no financeiro correspondente"
        actions={
          <OktonDateFilter
            value={{
              preset: filtroData[0],
              start: filtroData[1],
              end: filtroData[2],
            }}
            onChange={next =>
              setFiltroData([next.preset, next.start, next.end])
            }
          />
        }
      />

      {/* ─── Tabs ─── */}
      <Flex minH="0" w="100%" my="3" mt="4" gridGap={2}>
        <Button
          onClick={() => setTab('pending')}
          colorScheme="blue"
          variant={tab === 'pending' ? 'solid' : 'outline'}
          borderRadius="lg"
        >
          Pendentes ({lancamentosExtrato.length})
        </Button>
        <Button
          onClick={() => setTab('done')}
          colorScheme="blue"
          variant={tab === 'done' ? 'solid' : 'outline'}
          borderRadius="lg"
          borderWidth="2px"
        >
          Conciliados ({conciliados.length})
        </Button>
        <Flex borderWidth="1px" p="2" pb="1" ml="auto" rounded="2xl" bg={bg}>
          <HStack spacing={2}>
            <Box w="100%" minW="220px">
              <OktonSearchSelect
                name="filtroconta"
                placeholder="Selecione a conta"
                value={filterAccount}
                onChange={valor => {
                  setFilterAccount(valor);
                  setActiveBankId(null);
                  setBuscaExtrato('');
                }}
                options={contas.map(a => ({
                  value: a.id_cts,
                  label: a.descricao_cts,
                }))}
              />
            </Box>
            <Button
              leftIcon={<Upload size={18} />}
              variant="outline"
              colorScheme="blue"
              w="75%"
              onClick={() => history.push('/conciliacao/importar-ofx')}
              borderWidth="2px"
            >
              Importar OFX
            </Button>
            {bancoExtratoApi && (
              <Button
                leftIcon={<Landmark size={18} />}
                variant="outline"
                colorScheme="blue"
                w="75%"
                borderWidth="2px"
                onClick={() =>
                  history.push('/conciliacao/consultar-extrato', {
                    conta: filterAccount,
                    descricaoConta: contaFiltro?.descricao_cts ?? '',
                    banco: bancoExtratoApi,
                  })
                }
              >
                Atualizar Extrato
              </Button>
            )}
            <Button
              leftIcon={
                autoConciliar ? <Spinner size="sm" /> : <Zap size={18} />
              }
              colorScheme="green"
              w="75%"
              isDisabled={autoConciliar || lancamentosExtrato.length === 0}
              onClick={handleAutoMatch}
            >
              Auto Conciliar
            </Button>
          </HStack>
        </Flex>
      </Flex>

      {/* ─── Conteúdo ─── */}
      {semConta ? (
        <Flex
          borderWidth="1px"
          px="6"
          mt="1"
          py="16"
          rounded="2xl"
          bg={bg}
          align="center"
          justify="center"
          direction="column"
        >
          <Building2 size={48} color="#e2e8f0" />
          <Box textAlign="center" mt="4">
            <Text fontSize="sm" fontWeight="medium" color="gray.500">
              Nenhuma Conta Bancária cadastrada
            </Text>
            <Text fontSize="xs" color="gray.400" mt="1">
              A conciliação acontece dentro de uma Conta: cadastre uma para
              importar o extrato e casar os lançamentos.
            </Text>
          </Box>
          <Button
            mt="4"
            leftIcon={<Plus size={18} />}
            colorScheme="blue"
            onClick={() => history.push('/contas-bancarias/nova')}
          >
            Cadastrar Conta Bancária
          </Button>
        </Flex>
      ) : isLoading ? (
        <Flex>
          <OktonLoadingCard h="300px" />
        </Flex>
      ) : isError ? (
        <Flex>
          <OktonErrorCard h="300px" onRetry={recarregar} />
        </Flex>
      ) : tab === 'pending' ? (
        lancamentosExtrato.length === 0 ? (
          <Flex
            borderWidth="1px"
            px="6"
            mt="1"
            py="16"
            rounded="2xl"
            bg={bg}
            align="center"
            justify="center"
            direction="column"
          >
            <Building2 size={48} color="#e2e8f0" />
            <Box textAlign="center" mt="4">
              <Text fontSize="sm" fontWeight="medium" color="gray.500">
                Nenhum lançamento de extrato pendente
              </Text>
              <Text fontSize="xs" color="gray.400" mt="1">
                Importe um arquivo OFX do seu banco para começar
              </Text>
            </Box>
            <Button
              mt="4"
              leftIcon={<Upload size={18} />}
              colorScheme="blue"
              onClick={() => history.push('/conciliacao/importar-ofx')}
            >
              Importar OFX
            </Button>
          </Flex>
        ) : (
          // alignItems="start": nenhuma coluna estica a outra. Quem define a
          // altura e a direita, que a esquerda copia - e a lista do extrato rola
          // por dentro em vez de empurrar a linha toda para baixo.
          <SimpleGrid columns={[1, 1, 2]} spacing="4" mt="1" alignItems="start">
            {/* Coluna esquerda — extrato bancário */}
            <Flex
              bg={bg}
              borderWidth="1px"
              rounded="2xl"
              overflow="hidden"
              direction="column"
            >
              <Flex
                px="5"
                py="3.5"
                borderBottomWidth="1px"
                bg={subtleBg}
                align="center"
                gap="2"
              >
                <Building2 size={18} color="#3b82f6" />
                <Text fontSize="sm" fontWeight="semibold">
                  Extrato Bancário
                </Text>
                <Badge
                  ml="auto"
                  colorScheme="orange"
                  borderRadius="full"
                  px="2"
                >
                  {lancamentosExtrato.length} pendentes
                </Badge>
              </Flex>
              <Box px="3" py="2" borderBottomWidth="1px">
                <OktonInput
                  size="sm"
                  paddingStart="8"
                  leftElement={<Search size={14} color="#9ca3af" />}
                  name="conciliacao-busca-extrato"
                  aria-label="Buscar descrição, referência, valor..."
                  rounded="lg"
                  placeholder="Buscar descrição, referência, valor..."
                  value={buscaExtrato}
                  onChange={e => setBuscaExtrato(e.target.value)}
                />
              </Box>
              <Box flex="1" minH="0" overflowY="auto">
                {extratoComSugestao.length > 0 && (
                  <Box>
                    <Flex
                      px="4"
                      py="2"
                      bg={bg}
                      borderColor={sugestao2}
                      borderWidth="2px"
                      align="center"
                      gap="1.5"
                      gridGap={2}
                    >
                      <Sparkles size={14} color="#10b981" />
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="green.700"
                      >
                        Sugestão disponível
                      </Text>
                    </Flex>
                    {extratoComSugestao.map(tx => (
                      <LinhaExtrato
                        key={tx.id_exb}
                        tx={tx}
                        isActive={tx.id_exb === activeBankId}
                        isFlashing={tx.id_exb === flashId}
                        hasSugestao
                        onClick={() => {
                          if (tx.id_exb === activeBankId) {
                            setActiveBankId(null);
                            return;
                          }
                          setActiveBankId(tx.id_exb);
                          setAbaConsulta(abaDaSugestao(tx) ?? 'baixados');
                        }}
                      />
                    ))}
                  </Box>
                )}
                {extratoSemSugestao.length > 0 && (
                  <Box>
                    {extratoComSugestao.length > 0 && (
                      <Box px="4" py="2" borderWidth="2px" bg={subtleBg}>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color="gray.500"
                        >
                          Outros lançamentos
                        </Text>
                      </Box>
                    )}
                    {extratoSemSugestao.map(tx => (
                      <LinhaExtrato
                        key={tx.id_exb}
                        tx={tx}
                        isActive={tx.id_exb === activeBankId}
                        isFlashing={tx.id_exb === flashId}
                        hasSugestao={false}
                        onClick={() => {
                          if (tx.id_exb === activeBankId) {
                            setActiveBankId(null);
                            return;
                          }
                          setActiveBankId(tx.id_exb);
                          setAbaConsulta(abaDaSugestao(tx) ?? 'baixados');
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
              <OktonGridFooter
                total={extratoGrid.filtrados.length}
                page={extratoGrid.pagina}
                setPage={extratoGrid.setPagina}
                pageSize={extratoGrid.tamanhoPagina}
                setPageSize={extratoGrid.setTamanhoPagina}
                pageCount={extratoGrid.totalPaginas}
                itemLabel="lançamento"
                hideTotal
                hidePageSize
              />
            </Flex>

            {/* Coluna direita — seletor financeiro */}
            <Flex
              ref={colunaRef}
              bg={bg}
              borderWidth="1px"
              rounded="2xl"
              overflow="hidden"
              direction="column"
              position="relative"
            >
              {!activeLancamentoExtrato ? (
                <Flex
                  flex="1"
                  direction="column"
                  align="center"
                  justify="center"
                  py="20"
                  gap="3"
                  color="gray.400"
                >
                  <FileText size={40} color="#e2e8f0" />
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Selecione um lançamento do extrato
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    Os lançamentos financeiros disponíveis aparecerão aqui
                  </Text>
                </Flex>
              ) : (
                <>
                  {/* Informa o Extrato selecionado */}
                  <Flex
                    px="5"
                    py="2"
                    borderBottomWidth="1px"
                    bg={selecionadoLinha}
                    justify="space-between"
                    align="center"
                    gap="3"
                  >
                    <Box minW="0">
                      <Text fontWeight="bold">Título selecionado: </Text>
                      <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                        {activeLancamentoExtrato.descricao_exb}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatDataExtrato(activeLancamentoExtrato.data_exb)}
                      </Text>
                    </Box>
                    <Text
                      fontSize="sm"
                      fontWeight="black"
                      flexShrink={0}
                      color={
                        Number(activeLancamentoExtrato.valor_exb) >= 0
                          ? 'green.600'
                          : 'red.500'
                      }
                    >
                      {formatValue(activeLancamentoExtrato.valor_exb)}
                    </Text>
                  </Flex>

                  {/* Consulta usa um título que já existe; Cadastro cria o
                  título a partir deste lançamento do extrato e já concilia
                  os dois. */}
                  <Flex
                    borderBottomWidth="1px"
                    bg={subtleBg}
                    px="3"
                    py="2"
                    gridGap={2}
                  >
                    {(
                      [
                        ['consulta', 'Consulta'],
                        ['cadastro', 'Cadastro'],
                      ] as const
                    ).map(([aba, rotulo]) => (
                      <Button
                        key={aba}
                        size="sm"
                        flex="1"
                        px="2"
                        colorScheme="blue"
                        variant={abaDireita === aba ? 'solid' : 'outline'}
                        borderWidth={abaDireita === aba ? undefined : '2px'}
                        onClick={() => {
                          setAbaDireita(aba);
                          setErroCadastro('');
                        }}
                      >
                        {rotulo}
                      </Button>
                    ))}
                  </Flex>

                  {abaDireita === 'consulta' && (
                    <>
                      {selecao.length > 0 && (
                        <Flex
                          align="center"
                          justify="space-between"
                          gap="2"
                          px="4"
                          py="2"
                          borderBottomWidth="1px"
                          bg={selecionadoLinha}
                          flexWrap="wrap"
                        >
                          <Box>
                            <Text fontSize="xs" fontWeight="semibold">
                              {selecao.length} título(s) —{' '}
                              {formatValue(totalSelecionado)}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {sobraSelecao > 0.004
                                ? `Faltam ${formatValue(
                                    sobraSelecao,
                                  )} do extrato`
                                : sobraSelecao < -0.004
                                ? `Passa ${formatValue(
                                    -sobraSelecao,
                                  )} do extrato`
                                : 'Fecha com o extrato'}
                            </Text>
                            {/* A diferença não impede: cada título é baixado
                            por inteiro, pelo próprio saldo. */}
                            {Math.abs(sobraSelecao) > 0.004 && (
                              <Text fontSize="xs" color="gray.500">
                                Cada título é baixado por inteiro.
                              </Text>
                            )}
                          </Box>
                          <Flex gap="2">
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => setSelecao([])}
                            >
                              Limpar
                            </Button>
                            <Button
                              size="xs"
                              colorScheme="blue"
                              leftIcon={
                                variosMutation.isLoading ? (
                                  <Spinner size="xs" />
                                ) : (
                                  <Link2 size={14} />
                                )
                              }
                              isDisabled={
                                selecao.length === 0 || variosMutation.isLoading
                              }
                              onClick={confirmarSelecao}
                            >
                              Conciliar
                            </Button>
                          </Flex>
                        </Flex>
                      )}

                      <Flex borderBottomWidth="1px" px="3" py="2" gridGap={2}>
                        {ABAS_CONSULTA.map(([aba, rotulo]) => (
                          <Button
                            key={aba}
                            size="xs"
                            flex="1"
                            px="2"
                            colorScheme="blue"
                            variant={abaConsulta === aba ? 'solid' : 'outline'}
                            borderWidth={
                              abaConsulta === aba ? undefined : '2px'
                            }
                            onClick={() => mudarAbaConsulta(aba)}
                          >
                            {rotulo}
                          </Button>
                        ))}
                      </Flex>
                      <Box px="3" py="2" borderBottomWidth="1px">
                        <OktonInput
                          size="sm"
                          paddingStart="8"
                          leftElement={<Search size={14} color="#9ca3af" />}
                          name="conciliacao-busca-consulta"
                          aria-label={PLACEHOLDER_BUSCA[abaConsulta]}
                          color={color}
                          rounded="lg"
                          placeholder={PLACEHOLDER_BUSCA[abaConsulta]}
                          value={buscaConsulta}
                          onChange={e => setBuscaConsulta(e.target.value)}
                        />
                      </Box>

                      {abaConsulta === 'baixados' && (
                        <Box borderTopWidth="1px">
                          <Flex
                            align="center"
                            gap="1.5"
                            px="3"
                            py="2"
                            borderBottomWidth="1px"
                            bg={subtleBg}
                            gridGap={2}
                          >
                            <Text
                              fontSize="xs"
                              fontWeight="semibold"
                              color="gray.500"
                              textTransform="uppercase"
                            >
                              Baixados
                            </Text>
                            <Badge colorScheme="blue" borderRadius="full">
                              {baixadosCompativeis.length}
                            </Badge>
                            <BotaoOrdemValor
                              ordem={ordemBaixados}
                              setOrdem={setOrdemBaixados}
                            />
                          </Flex>
                          <Box>
                            {suggestionsFiltradas.length === 0 &&
                            outrosGrid.filtrados.length === 0 ? (
                              <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                py="12"
                                gap="2"
                                color="gray.400"
                              >
                                <FileText size={40} color="#e2e8f0" />
                                <Text fontSize="sm">
                                  Nenhum lançamento financeiro pendente
                                </Text>
                              </Flex>
                            ) : (
                              <>
                                {suggestionsFiltradas.length > 0 && (
                                  <Box>
                                    <Flex
                                      px="4"
                                      py="2"
                                      bg={sugestao2}
                                      borderBottomWidth="1px"
                                      align="center"
                                      gap="1.5"
                                      gridGap={2}
                                    >
                                      <Sparkles size={14} color="#10b981" />
                                      <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        color="green.700"
                                      >
                                        Sugestão automática — mesmo valor, data
                                        próxima
                                      </Text>
                                    </Flex>
                                    {suggestionsFiltradas.map(tx => (
                                      <FinancialRow
                                        key={tx.id_fin}
                                        tx={tx}
                                        bankAmount={Number(
                                          activeLancamentoExtrato.valor_exb,
                                        )}
                                        linking={linkingId === tx.id_fin}
                                        highlight
                                        onClick={() => handleLink(tx.id_fin)}
                                        selecionado={
                                          !!selecao.find(
                                            s => s.id === tx.id_fin,
                                          )
                                        }
                                        onSelecionar={() =>
                                          alternaSelecao(
                                            tx.id_fin,
                                            Number(tx.valorbaixa_fin),
                                            false,
                                          )
                                        }
                                      />
                                    ))}
                                  </Box>
                                )}
                                {outrosGrid.filtrados.length > 0 && (
                                  <Box>
                                    {suggestionsFiltradas.length > 0 && (
                                      <Box
                                        px="4"
                                        py="2"
                                        borderBottomWidth="1px"
                                        bg={subtleBg}
                                      >
                                        <Text
                                          fontSize="xs"
                                          fontWeight="semibold"
                                          color="gray.500"
                                        >
                                          Outros lançamentos
                                        </Text>
                                      </Box>
                                    )}
                                    {outrosGrid.itens.map(tx => (
                                      <FinancialRow
                                        key={tx.id_fin}
                                        tx={tx}
                                        bankAmount={Number(
                                          activeLancamentoExtrato.valor_exb,
                                        )}
                                        linking={linkingId === tx.id_fin}
                                        highlight={false}
                                        onClick={() => handleLink(tx.id_fin)}
                                        selecionado={
                                          !!selecao.find(
                                            s => s.id === tx.id_fin,
                                          )
                                        }
                                        onSelecionar={() =>
                                          alternaSelecao(
                                            tx.id_fin,
                                            Number(tx.valorbaixa_fin),
                                            false,
                                          )
                                        }
                                      />
                                    ))}
                                    <OktonGridFooter
                                      total={outrosGrid.filtrados.length}
                                      page={outrosGrid.pagina}
                                      setPage={outrosGrid.setPagina}
                                      pageSize={outrosGrid.tamanhoPagina}
                                      setPageSize={outrosGrid.setTamanhoPagina}
                                      pageCount={outrosGrid.totalPaginas}
                                      itemLabel="lançamento"
                                      hideTotal
                                      hidePageSize
                                    />
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      )}

                      {abaConsulta === 'aberto' && (
                        <Box borderTopWidth="1px">
                          <Flex
                            align="center"
                            gap="1.5"
                            px="3"
                            py="2"
                            borderBottomWidth="1px"
                            bg={subtleBg}
                            gridGap={2}
                          >
                            <Text
                              fontSize="xs"
                              fontWeight="semibold"
                              color="gray.500"
                              textTransform="uppercase"
                            >
                              Em aberto
                            </Text>
                            <Badge colorScheme="blue" borderRadius="full">
                              {abertosDoPeriodo.length}
                            </Badge>
                            <BotaoOrdemValor
                              ordem={ordemAbertos}
                              setOrdem={setOrdemAbertos}
                            />
                          </Flex>
                          <Box>
                            {abertosDoPeriodo.length === 0 ? (
                              <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                py="12"
                                gap="2"
                                color="gray.400"
                              >
                                <FileText size={40} color="#e2e8f0" />
                                <Text fontSize="sm">
                                  Nenhum título em aberto no período
                                </Text>
                              </Flex>
                            ) : (
                              <>
                                {abertosGrid.itens.map(tx => {
                                  const selecionado =
                                    abertoSelecionado === tx.id_fin;
                                  // O extrato vem com sinal; o saldo do título é
                                  // sempre positivo.
                                  const diferenca =
                                    Number(tx.saldo_fin) -
                                    Math.abs(
                                      Number(activeLancamentoExtrato.valor_exb),
                                    );
                                  const fecha = Math.abs(diferenca) < 0.005;
                                  return (
                                    <Box
                                      key={tx.id_fin}
                                      borderBottomWidth="1px"
                                      _last={{ borderBottomWidth: 0 }}
                                    >
                                      {/* O fundo fica aqui, e não no Flex
                                      clicável: lá ele começava depois da caixa
                                      de seleção, e a faixa parava antes dela. */}
                                      <Flex
                                        align="center"
                                        bg={
                                          selecionado
                                            ? selecionadoLinha
                                            : tx.sugerido
                                            ? sugestaoLinha
                                            : undefined
                                        }
                                      >
                                        <Box pl="4" flexShrink={0}>
                                          <OktonCheckbox
                                            name={`conciliacao-aberto-${tx.id_fin}`}
                                            label={null}
                                            size="md"
                                            aria-label="Selecionar título"
                                            isChecked={
                                              !!selecao.find(
                                                s => s.id === tx.id_fin,
                                              )
                                            }
                                            onChange={() =>
                                              alternaSelecao(
                                                tx.id_fin,
                                                Number(tx.saldo_fin),
                                                true,
                                              )
                                            }
                                          />
                                        </Box>
                                        <Flex
                                          role="button"
                                          tabIndex={0}
                                          flex="1"
                                          minW="0"
                                          h={ALTURA_LINHA_FINANCEIRO}
                                          overflow="hidden"
                                          align="center"
                                          gap="3"
                                          px="5"
                                          py="3.5"
                                          cursor="pointer"
                                          _hover={{
                                            bg:
                                              !selecionado && tx.sugerido
                                                ? hoverSugestao
                                                : hoverLinha,
                                          }}
                                          onClick={() =>
                                            setAbertoSelecionado(
                                              selecionado ? null : tx.id_fin,
                                            )
                                          }
                                        >
                                          <Flex
                                            flexShrink={0}
                                            w="8"
                                            h="8"
                                            mr="4"
                                            borderRadius="full"
                                            align="center"
                                            justify="center"
                                            bg={
                                              fecha ? 'green.100' : 'gray.100'
                                            }
                                          >
                                            <Clock
                                              size={15}
                                              color={
                                                fecha ? '#047857' : '#71717a'
                                              }
                                            />
                                          </Flex>
                                          <Box flex="1" minW="0">
                                            <Text
                                              fontSize="sm"
                                              fontWeight="semibold"
                                              noOfLines={1}
                                            >
                                              {tx.nome_cli ??
                                                tx.observacao_fin ??
                                                'Título'}
                                            </Text>
                                            <Text
                                              fontSize="xs"
                                              color="gray.500"
                                              mt="0.5"
                                              noOfLines={1}
                                            >
                                              Vencimento{' '}
                                              {formatCustomDate(
                                                tx.vencimento_fin.slice(0, 10),
                                                'yyyy-MM-dd',
                                                'dd/MM/yyyy',
                                              )}
                                              {tx.nome_cdc
                                                ? ` • ${tx.nome_cdc}`
                                                : ''}
                                              {tx.documento_fin
                                                ? ` • ${tx.documento_fin}`
                                                : ''}
                                            </Text>
                                          </Box>
                                          <Box
                                            flexShrink={0}
                                            textAlign="right"
                                            whiteSpace="nowrap"
                                          >
                                            <Text
                                              fontSize="sm"
                                              fontWeight="bold"
                                              color={
                                                tx.tipo_fin === TIPO_FIN_RECEBER
                                                  ? 'green.600'
                                                  : 'red.500'
                                              }
                                            >
                                              {formatValue(
                                                Number(tx.saldo_fin),
                                              )}
                                            </Text>
                                            {!fecha && (
                                              <Text
                                                fontSize="sm"
                                                color="orange.500"
                                                fontWeight="medium"
                                              >
                                                Dif:{' '}
                                                {formatValue(
                                                  Math.abs(diferenca),
                                                )}
                                              </Text>
                                            )}
                                          </Box>
                                        </Flex>
                                      </Flex>

                                      {/* A baixa precisa de três coisas que o título
                                em aberto ainda não tem: por onde o dinheiro
                                entrou, em qual Caixa e o histórico da baixa. */}
                                      {selecionado && (
                                        <Stack
                                          spacing="3"
                                          px="5"
                                          pb="4"
                                          bg={selecionadoLinha}
                                        >
                                          {/* Valores diferentes não impedem:
                                          quem concilia é que diz se aquele
                                          lançamento explica aquele título. O
                                          que a tela faz é deixar a diferença à
                                          vista antes de gravar. */}
                                          {!fecha && (
                                            <Box
                                              bg="orange.50"
                                              color="orange.700"
                                              fontSize="xs"
                                              borderRadius="lg"
                                              px="3"
                                              py="2"
                                            >
                                              O extrato é{' '}
                                              {formatValue(
                                                Math.abs(
                                                  Number(
                                                    activeLancamentoExtrato.valor_exb,
                                                  ),
                                                ),
                                              )}{' '}
                                              e o saldo do título é{' '}
                                              {formatValue(
                                                Number(tx.saldo_fin),
                                              )}
                                              , uma diferença de{' '}
                                              {formatValue(Math.abs(diferenca))}
                                              . O título será baixado por
                                              inteiro, pelo próprio saldo.
                                            </Box>
                                          )}
                                          <Box>
                                            <Text
                                              as="label"
                                              htmlFor="baixamoeda"
                                              display="block"
                                              fontSize="sm"
                                              fontWeight="medium"
                                              mb="1"
                                            >
                                              Moeda
                                            </Text>
                                            <OktonSearchSelect
                                              name="baixamoeda"
                                              placeholder="Moeda do banco (padrão)"
                                              value={formBaixa.moeda}
                                              onChange={valor =>
                                                setFormBaixa(f => ({
                                                  ...f,
                                                  moeda: valor,
                                                }))
                                              }
                                              options={moedasDaConta
                                                .filter(
                                                  m => m.principal_ctm !== '1',
                                                )
                                                .map(m => ({
                                                  value: m.moeda_ctm,
                                                  label: m.nome_moe,
                                                }))}
                                            />
                                          </Box>

                                          <CaixaField
                                            data={dataDoExtrato}
                                            valor={formBaixa.caixa}
                                            onChange={caixa =>
                                              setFormBaixa(f => ({
                                                ...f,
                                                caixa,
                                              }))
                                            }
                                          />

                                          <Box>
                                            <Text
                                              as="label"
                                              htmlFor={`conciliacao-baixa-observacao-${tx.id_fin}`}
                                              display="block"
                                              fontSize="sm"
                                              fontWeight="medium"
                                              mb="1"
                                            >
                                              Observação da baixa
                                            </Text>
                                            <OktonInput
                                              name={`conciliacao-baixa-observacao-${tx.id_fin}`}
                                              color={color}
                                              size="lg"
                                              rounded="2xl"
                                              fontWeight="light"
                                              bg={bg}
                                              placeholder={
                                                activeLancamentoExtrato.descricao_exb
                                              }
                                              maxLength={1024}
                                              value={formBaixa.observacao}
                                              onChange={e => {
                                                const valor = e.target.value;
                                                setFormBaixa(f => ({
                                                  ...f,
                                                  observacao: valor,
                                                }));
                                              }}
                                            />
                                            <Text
                                              fontSize="xs"
                                              color="gray.500"
                                              mt="1"
                                            >
                                              Em branco, usa o histórico do
                                              extrato.
                                            </Text>
                                          </Box>

                                          <Button
                                            colorScheme="blue"
                                            leftIcon={
                                              baixaMutation.isLoading ? (
                                                <Spinner size="sm" />
                                              ) : (
                                                <Link2 size={16} />
                                              )
                                            }
                                            isDisabled={baixaMutation.isLoading}
                                            onClick={() =>
                                              baixarEConciliar(tx.id_fin)
                                            }
                                          >
                                            Baixar e conciliar
                                          </Button>
                                        </Stack>
                                      )}
                                    </Box>
                                  );
                                })}
                              </>
                            )}
                          </Box>
                          <OktonGridFooter
                            total={abertosGrid.filtrados.length}
                            page={abertosGrid.pagina}
                            setPage={abertosGrid.setPagina}
                            pageSize={abertosGrid.tamanhoPagina}
                            setPageSize={abertosGrid.setTamanhoPagina}
                            pageCount={abertosGrid.totalPaginas}
                            itemLabel="título"
                            itemLabelPlural="títulos"
                            hideTotal
                            hidePageSize
                          />
                        </Box>
                      )}

                      {abaConsulta === 'saquesDepositos' && (
                        <SimpleGrid
                          columns={2}
                          spacing="0"
                          borderTopWidth="1px"
                        >
                          <Box minW="0" borderRightWidth="1px">
                            <Flex
                              align="center"
                              gap="1.5"
                              px="3"
                              py="2"
                              borderBottomWidth="1px"
                              bg={subtleBg}
                              gridGap={2}
                            >
                              <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color="gray.500"
                                textTransform="uppercase"
                              >
                                Saques
                              </Text>
                              <Badge colorScheme="blue" borderRadius="full">
                                {saquesDisponiveis.length}
                              </Badge>
                            </Flex>
                            <Box>
                              {saquesGrid.filtrados.length === 0 ? (
                                <Flex
                                  direction="column"
                                  align="center"
                                  justify="center"
                                  py="12"
                                  gap="2"
                                  color="gray.400"
                                >
                                  <Banknote size={40} color="#e2e8f0" />
                                  <Text fontSize="sm">
                                    Nenhum Saque disponível
                                  </Text>
                                </Flex>
                              ) : (
                                saquesGrid.itens.map(doc => (
                                  <Flex
                                    key={doc.id}
                                    role="button"
                                    tabIndex={0}
                                    cursor="pointer"
                                    w="100%"
                                    h={ALTURA_LINHA_FINANCEIRO}
                                    overflow="hidden"
                                    align="center"
                                    gap="3"
                                    px="5"
                                    py="3.5"
                                    textAlign="left"
                                    borderBottomWidth="1px"
                                    _last={{ borderBottomWidth: 0 }}
                                    bg={
                                      doc.sugerido ? sugestaoLinha : undefined
                                    }
                                    _hover={{
                                      bg: doc.sugerido
                                        ? hoverSugestao
                                        : hoverLinha,
                                    }}
                                    opacity={linkDocumento.isLoading ? 0.7 : 1}
                                    pointerEvents={
                                      linkDocumento.isLoading ? 'none' : 'auto'
                                    }
                                    onClick={() =>
                                      handleLinkDocumento(doc.tipo, doc.id)
                                    }
                                  >
                                    <Flex
                                      flexShrink={0}
                                      w="8"
                                      h="8"
                                      mr="4"
                                      borderRadius="full"
                                      align="center"
                                      justify="center"
                                      bg={
                                        doc.sugerido ? 'green.100' : 'gray.100'
                                      }
                                    >
                                      <Banknote size={16} color="#9ca3af" />
                                    </Flex>
                                    <Box flex="1" minW="0">
                                      <Text
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        noOfLines={1}
                                      >
                                        {doc.descricao}
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        color="gray.500"
                                        mt="0.5"
                                        noOfLines={1}
                                      >
                                        {formatCustomDate(
                                          doc.data.slice(0, 10),
                                          'yyyy-MM-dd',
                                          'dd/MM/yyyy',
                                        )}
                                        {doc.sugerido
                                          ? ' • mesmo valor, data próxima'
                                          : ''}
                                      </Text>
                                    </Box>
                                    <Text
                                      fontSize="sm"
                                      fontWeight="bold"
                                      flexShrink={0}
                                    >
                                      {formatValue(Number(doc.valor))}
                                    </Text>
                                  </Flex>
                                ))
                              )}
                            </Box>
                            <OktonGridFooter
                              total={saquesGrid.filtrados.length}
                              page={saquesGrid.pagina}
                              setPage={saquesGrid.setPagina}
                              pageSize={saquesGrid.tamanhoPagina}
                              setPageSize={saquesGrid.setTamanhoPagina}
                              pageCount={saquesGrid.totalPaginas}
                              itemLabel="saque"
                              hideTotal
                              hidePageSize
                            />
                          </Box>
                          <Box minW="0">
                            <Flex
                              align="center"
                              gap="1.5"
                              px="3"
                              py="2"
                              borderBottomWidth="1px"
                              bg={subtleBg}
                              gridGap={2}
                            >
                              <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color="gray.500"
                                textTransform="uppercase"
                              >
                                Depósitos
                              </Text>
                              <Badge colorScheme="blue" borderRadius="full">
                                {depositosDisponiveis.length}
                              </Badge>
                            </Flex>
                            <Box>
                              {depositosGrid.filtrados.length === 0 ? (
                                <Flex
                                  direction="column"
                                  align="center"
                                  justify="center"
                                  py="12"
                                  gap="2"
                                  color="gray.400"
                                >
                                  <PiggyBank size={40} color="#e2e8f0" />
                                  <Text fontSize="sm">
                                    Nenhum Depósito disponível
                                  </Text>
                                </Flex>
                              ) : (
                                depositosGrid.itens.map(doc => (
                                  <Flex
                                    key={doc.id}
                                    role="button"
                                    tabIndex={0}
                                    cursor="pointer"
                                    w="100%"
                                    h={ALTURA_LINHA_FINANCEIRO}
                                    overflow="hidden"
                                    align="center"
                                    gap="3"
                                    px="5"
                                    py="3.5"
                                    textAlign="left"
                                    borderBottomWidth="1px"
                                    _last={{ borderBottomWidth: 0 }}
                                    bg={
                                      doc.sugerido ? sugestaoLinha : undefined
                                    }
                                    _hover={{
                                      bg: doc.sugerido
                                        ? hoverSugestao
                                        : hoverLinha,
                                    }}
                                    opacity={linkDocumento.isLoading ? 0.7 : 1}
                                    pointerEvents={
                                      linkDocumento.isLoading ? 'none' : 'auto'
                                    }
                                    onClick={() =>
                                      handleLinkDocumento(doc.tipo, doc.id)
                                    }
                                  >
                                    <Flex
                                      flexShrink={0}
                                      w="8"
                                      h="8"
                                      mr="4"
                                      borderRadius="full"
                                      align="center"
                                      justify="center"
                                      bg={
                                        doc.sugerido ? 'green.100' : 'gray.100'
                                      }
                                    >
                                      <PiggyBank size={16} color="#9ca3af" />
                                    </Flex>
                                    <Box flex="1" minW="0">
                                      <Text
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        noOfLines={1}
                                      >
                                        {doc.descricao}
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        color="gray.500"
                                        mt="0.5"
                                        noOfLines={1}
                                      >
                                        {formatCustomDate(
                                          doc.data.slice(0, 10),
                                          'yyyy-MM-dd',
                                          'dd/MM/yyyy',
                                        )}
                                        {doc.sugerido
                                          ? ' • mesmo valor, data próxima'
                                          : ''}
                                      </Text>
                                    </Box>
                                    <Text
                                      fontSize="sm"
                                      fontWeight="bold"
                                      flexShrink={0}
                                    >
                                      {formatValue(Number(doc.valor))}
                                    </Text>
                                  </Flex>
                                ))
                              )}
                            </Box>
                            <OktonGridFooter
                              total={depositosGrid.filtrados.length}
                              page={depositosGrid.pagina}
                              setPage={depositosGrid.setPagina}
                              pageSize={depositosGrid.tamanhoPagina}
                              setPageSize={depositosGrid.setTamanhoPagina}
                              pageCount={depositosGrid.totalPaginas}
                              itemLabel="depósito"
                              hideTotal
                              hidePageSize
                            />
                          </Box>
                        </SimpleGrid>
                      )}

                      {abaConsulta === 'transferencias' && (
                        <Box borderTopWidth="1px">
                          <Flex
                            align="center"
                            gap="1.5"
                            px="3"
                            py="2"
                            borderBottomWidth="1px"
                            bg={subtleBg}
                            gridGap={2}
                          >
                            <Text
                              fontSize="xs"
                              fontWeight="semibold"
                              color="gray.500"
                              textTransform="uppercase"
                            >
                              Transferências entre contas
                            </Text>
                            <Badge colorScheme="blue" borderRadius="full">
                              {transferenciasDisponiveis.length}
                            </Badge>
                          </Flex>
                          <Box>
                            {transferenciasGrid.filtrados.length === 0 ? (
                              <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                py="12"
                                gap="2"
                                color="gray.400"
                              >
                                <ArrowLeftRight size={40} color="#e2e8f0" />
                                <Text fontSize="sm">
                                  Nenhuma Transferência disponível
                                </Text>
                              </Flex>
                            ) : (
                              transferenciasGrid.itens.map(tba => (
                                <Flex
                                  key={tba.id_tba}
                                  role="button"
                                  tabIndex={0}
                                  cursor="pointer"
                                  w="100%"
                                  h={ALTURA_LINHA_FINANCEIRO}
                                  overflow="hidden"
                                  align="center"
                                  gap="3"
                                  px="5"
                                  py="3.5"
                                  textAlign="left"
                                  borderBottomWidth="1px"
                                  _last={{ borderBottomWidth: 0 }}
                                  bg={tba.sugerida ? sugestaoLinha : undefined}
                                  _hover={{
                                    bg: tba.sugerida
                                      ? hoverSugestao
                                      : hoverLinha,
                                  }}
                                  opacity={
                                    linkTransferencia.isLoading ? 0.7 : 1
                                  }
                                  pointerEvents={
                                    linkTransferencia.isLoading
                                      ? 'none'
                                      : 'auto'
                                  }
                                  onClick={() =>
                                    handleLinkTransferencia(tba.id_tba)
                                  }
                                >
                                  <Flex
                                    flexShrink={0}
                                    w="8"
                                    h="8"
                                    mr="4"
                                    borderRadius="full"
                                    align="center"
                                    justify="center"
                                    bg={tba.sugerida ? 'green.100' : 'gray.100'}
                                  >
                                    <ArrowLeftRight size={16} color="#9ca3af" />
                                  </Flex>
                                  <Box flex="1" minW="0">
                                    <Text
                                      fontSize="sm"
                                      fontWeight="semibold"
                                      noOfLines={1}
                                    >
                                      {tba.observacao_tba ??
                                        tba.documento_tba ??
                                        'Transferência'}
                                    </Text>
                                    <Text
                                      fontSize="xs"
                                      color="gray.500"
                                      mt="0.5"
                                      noOfLines={1}
                                    >
                                      {formatCustomDate(
                                        tba.data_tba.slice(0, 10),
                                        'yyyy-MM-dd',
                                        'dd/MM/yyyy',
                                      )}{' '}
                                      • {tba.origemdescricao_tba ?? '?'} →{' '}
                                      {tba.destinodescricao_tba ?? '?'}
                                    </Text>
                                  </Box>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="bold"
                                    flexShrink={0}
                                  >
                                    {formatValue(Number(tba.valor_tba))}
                                  </Text>
                                </Flex>
                              ))
                            )}
                          </Box>
                          <OktonGridFooter
                            total={transferenciasGrid.filtrados.length}
                            page={transferenciasGrid.pagina}
                            setPage={transferenciasGrid.setPagina}
                            pageSize={transferenciasGrid.tamanhoPagina}
                            setPageSize={transferenciasGrid.setTamanhoPagina}
                            pageCount={transferenciasGrid.totalPaginas}
                            itemLabel="transferência"
                            itemLabelPlural="transferências"
                            hideTotal
                            hidePageSize
                          />
                        </Box>
                      )}
                    </>
                  )}

                  {/* Sempre montado: quando não é a aba ativa sai do fluxo
                  (position absolute) e fica invisível. */}
                  <Stack
                    spacing="4"
                    px="5"
                    py="4"
                    position={abaDireita === 'cadastro' ? 'static' : 'absolute'}
                    visibility={
                      abaDireita === 'cadastro' ? 'visible' : 'hidden'
                    }
                    pointerEvents={
                      abaDireita === 'cadastro' ? undefined : 'none'
                    }
                    left="0"
                    right="0"
                    aria-hidden={abaDireita !== 'cadastro'}
                  >
                    <Box>
                      <Text
                        as="label"
                        htmlFor="tipocadastro"
                        display="block"
                        fontSize="sm"
                        fontWeight="medium"
                        mb="1"
                      >
                        Tipo de lançamento
                      </Text>
                      <OktonSelect
                        size="lg"
                        rounded="2xl"
                        name="tipocadastro"
                        color={color}
                        value={tipoCadastro}
                        onChange={e => {
                          setTipoCadastro(e.target.value as TipoCadastro);
                          setErroCadastro('');
                          // Os cheques pertenciam ao Depósito que estava
                          // sendo montado: trocar o tipo desfaz a escolha.
                          limpaCheques();
                        }}
                      >
                        <option value="lancamento">Lançamento</option>
                        <option value="transferencia">Transferência</option>
                        <option value="saque">Saque</option>
                        <option value="deposito">Depósito</option>
                      </OktonSelect>
                      <Text fontSize="xs" color="gray.500" mt="1">
                        {DESCRICAO_CADASTRO[tipoCadastro]}
                      </Text>
                    </Box>

                    {!!erroCadastro && (
                      <OktonAlert status="error" description={erroCadastro} />
                    )}

                    {tipoCadastro === 'lancamento' && (
                      <>
                        <Box>
                          <Text
                            as="label"
                            htmlFor="cliente"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Cliente *
                          </Text>
                          <OktonSearchSelect
                            filterOptions={false}
                            name="cliente"
                            value={formGerar.cliente}
                            onChange={cliente =>
                              setFormGerar(f => ({ ...f, cliente }))
                            }
                            search={buscaCliente}
                            onSearchChange={setBuscaCliente}
                            loading={isClientesFetching}
                            placeholder="Nome, código ou CPF/CNPJ"
                            emptyMessage={
                              buscaCliente.trim().length < 2
                                ? 'Digite ao menos 2 caracteres para buscar'
                                : 'Nenhum cliente encontrado'
                            }
                            options={[
                              { value: CLIENTE_NOVO, label: 'Novo Cliente' },
                              ...clientes.map(c => ({
                                value: c.id_cli,
                                label: c.nome_cli,
                              })),
                            ]}
                          />
                        </Box>

                        {formGerar.cliente === CLIENTE_NOVO && (
                          <Box borderWidth="1px" rounded="2xl" p="4">
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              textTransform="uppercase"
                              mb="3"
                            >
                              Novo Cliente
                            </Text>
                            <Stack spacing="4">
                              <Box>
                                <Text fontSize="sm" fontWeight="medium" mb="1">
                                  Tipo *
                                </Text>
                                <HStack overflow="hidden" spacing={2} p="1">
                                  <Button
                                    flex="1"
                                    borderRadius="none"
                                    variant={
                                      formGerar.clientenovotipo ===
                                      TIPO_CLIENTE_PF
                                        ? 'solid'
                                        : 'outline'
                                    }
                                    onClick={() =>
                                      setFormGerar(f => ({
                                        ...f,
                                        clientenovotipo: TIPO_CLIENTE_PF,
                                      }))
                                    }
                                  >
                                    Pessoa Física
                                  </Button>
                                  <Button
                                    flex="1"
                                    borderRadius="none"
                                    variant={
                                      formGerar.clientenovotipo ===
                                      TIPO_CLIENTE_PJ
                                        ? 'solid'
                                        : 'outline'
                                    }
                                    onClick={() =>
                                      setFormGerar(f => ({
                                        ...f,
                                        clientenovotipo: TIPO_CLIENTE_PJ,
                                      }))
                                    }
                                  >
                                    Pessoa Jurídica
                                  </Button>
                                </HStack>
                              </Box>
                              <Box>
                                <Text
                                  as="label"
                                  htmlFor="conciliacao-cliente-nome"
                                  display="block"
                                  fontSize="sm"
                                  fontWeight="medium"
                                  mb="1"
                                >
                                  Nome *
                                </Text>
                                <OktonInput
                                  name="conciliacao-cliente-nome"
                                  color={color}
                                  maxLength={60}
                                  value={formGerar.clientenovonome}
                                  onChange={e => {
                                    const valor = e.target.value;
                                    nomeClienteDigitadoRef.current = true;
                                    setFormGerar(f => ({
                                      ...f,
                                      clientenovonome: valor,
                                    }));
                                  }}
                                />
                              </Box>
                              <Box>
                                <Text
                                  as="label"
                                  htmlFor="conciliacao-cliente-documento"
                                  display="block"
                                  fontSize="sm"
                                  fontWeight="medium"
                                  mb="1"
                                >
                                  {formGerar.clientenovotipo === TIPO_CLIENTE_PJ
                                    ? 'CNPJ *'
                                    : 'CPF'}
                                </Text>
                                <OktonInput
                                  name="conciliacao-cliente-documento"
                                  color={color}
                                  maxLength={18}
                                  placeholder={
                                    formGerar.clientenovotipo ===
                                    TIPO_CLIENTE_PJ
                                      ? 'Só números'
                                      : 'Opcional'
                                  }
                                  value={formataDocumento(
                                    formGerar.clientenovodocumento,
                                  )}
                                  onChange={e => {
                                    const valor = e.target.value;
                                    setFormGerar(f => ({
                                      ...f,
                                      clientenovodocumento: soDigitos(valor),
                                    }));
                                  }}
                                />
                                {formGerar.clientenovotipo ===
                                  TIPO_CLIENTE_PJ && (
                                  <Text fontSize="xs" color="gray.500" mt="1">
                                    {enderecoNovoCliente
                                      ? 'Endereço encontrado e será cadastrado junto com o Cliente.'
                                      : 'Ao completar o CNPJ, o nome e o endereço são consultados automaticamente.'}
                                  </Text>
                                )}
                                {clienteComDocumento?.existe &&
                                  clienteComDocumento.cliente && (
                                    <Text
                                      fontSize="xs"
                                      color="orange.500"
                                      mt="1"
                                    >
                                      Este documento já pertence ao Cliente
                                      &quot;
                                      {clienteComDocumento.cliente.nome_cli}
                                      &quot; (código{' '}
                                      {clienteComDocumento.cliente.id_cli}).
                                      Busque por ele no campo Cliente, em vez de
                                      cadastrar um novo.
                                    </Text>
                                  )}
                              </Box>
                              {formGerar.clientenovotipo ===
                                TIPO_CLIENTE_PJ && (
                                <Box>
                                  <Text
                                    as="label"
                                    htmlFor="conciliacao-cliente-ie"
                                    display="block"
                                    fontSize="sm"
                                    fontWeight="medium"
                                    mb="1"
                                  >
                                    Inscrição Estadual
                                  </Text>
                                  <OktonInput
                                    name="conciliacao-cliente-ie"
                                    color={color}
                                    maxLength={15}
                                    placeholder="Opcional — sem informar, o Cliente é gravado como Isento"
                                    value={formGerar.clientenovoie}
                                    onChange={e => {
                                      const valor = e.target.value;
                                      setFormGerar(f => ({
                                        ...f,
                                        clientenovoie: valor,
                                      }));
                                    }}
                                  />
                                </Box>
                              )}
                            </Stack>
                          </Box>
                        )}

                        <Box>
                          <Text
                            as="label"
                            htmlFor="conciliacao-lancamento-referencia"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Referência *
                          </Text>
                          <OktonInput
                            name="conciliacao-lancamento-referencia"
                            color={color}
                            size="lg"
                            rounded="2xl"
                            fontWeight="light"
                            placeholder="Nº doc / NF"
                            maxLength={40}
                            value={formGerar.documento}
                            onChange={e => {
                              const valor = e.target.value;
                              setFormGerar(f => ({
                                ...f,
                                documento: valor,
                              }));
                            }}
                          />
                        </Box>

                        <Box>
                          <Text
                            as="label"
                            htmlFor="gerarmoeda"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Moeda
                          </Text>
                          <OktonSearchSelect
                            name="gerarmoeda"
                            placeholder="Moeda do banco (padrão)"
                            value={formGerar.moeda}
                            onChange={valor =>
                              setFormGerar(f => ({
                                ...f,
                                moeda: valor,
                              }))
                            }
                            options={[
                              ...moedasDaConta
                                .filter(m => m.principal_ctm !== '1')
                                .map(m => ({
                                  value: m.moeda_ctm,
                                  label: m.nome_moe,
                                })),
                            ]}
                          />
                          <Text fontSize="xs" color="gray.500" mt="1">
                            Registra por onde o dinheiro entrou.
                          </Text>
                        </Box>

                        <CaixaField
                          data={dataDoExtrato}
                          valor={formGerar.caixa}
                          onChange={caixa =>
                            setFormGerar(f => ({ ...f, caixa }))
                          }
                        />

                        {/* Direcionador, Departamento, Centro de Custo e
                        Observação ficam guardados: a maior parte dos
                        lançamentos nasce da conciliação sem eles, e pedir os
                        oito campos de uma vez trava quem só quer registrar o
                        título. */}
                        <Button
                          variant="ghost"
                          size="sm"
                          alignSelf="flex-start"
                          colorScheme="blue"
                          leftIcon={
                            mostrarClassificacoes ? (
                              <Minus size={16} />
                            ) : (
                              <Plus size={16} />
                            )
                          }
                          onClick={() => setMostrarClassificacoes(v => !v)}
                        >
                          Classificações adicionais
                          {!mostrarClassificacoes &&
                            classificacoesPreenchidas > 0 && (
                              <Badge
                                ml="2"
                                colorScheme="blue"
                                borderRadius="full"
                              >
                                {classificacoesPreenchidas}
                              </Badge>
                            )}
                        </Button>

                        {mostrarClassificacoes && (
                          <>
                            <Box>
                              <Text
                                as="label"
                                htmlFor="gerardirecionador"
                                display="block"
                                fontSize="sm"
                                fontWeight="medium"
                                mb="1"
                              >
                                Direcionador {controlaDRE && '*'}
                              </Text>
                              <OktonSearchSelect
                                name="gerardirecionador"
                                placeholder="Sem direcionador"
                                value={formGerar.direcionador}
                                onChange={valor =>
                                  setFormGerar(f => ({
                                    ...f,
                                    direcionador: valor,
                                  }))
                                }
                                options={[
                                  {
                                    value: DIRECIONADOR_NOVO,
                                    label: 'Novo Direcionador',
                                  },
                                  ...direcionadores.map(d => ({
                                    value: d.id_ori,
                                    label: d.nome_ori,
                                  })),
                                ]}
                              />
                            </Box>

                            {formGerar.direcionador === DIRECIONADOR_NOVO && (
                              <Box>
                                <Text
                                  as="label"
                                  htmlFor="conciliacao-direcionador-nome"
                                  display="block"
                                  fontSize="sm"
                                  fontWeight="medium"
                                  mb="1"
                                >
                                  Nome do novo Direcionador *
                                </Text>
                                <OktonInput
                                  name="conciliacao-direcionador-nome"
                                  color={color}
                                  size="lg"
                                  rounded="2xl"
                                  fontWeight="light"
                                  maxLength={120}
                                  value={formGerar.direcionadornovo}
                                  onChange={e => {
                                    const valor = e.target.value;
                                    setFormGerar(f => ({
                                      ...f,
                                      direcionadornovo: valor,
                                    }));
                                  }}
                                />
                              </Box>
                            )}

                            {formGerar.direcionador === DIRECIONADOR_NOVO && (
                              <Box>
                                <Text
                                  as="label"
                                  htmlFor="gerardirecionadorplano"
                                  display="block"
                                  fontSize="sm"
                                  fontWeight="medium"
                                  mb="1"
                                >
                                  Plano de Contas (DFC) do Direcionador{' '}
                                  {controlaDRE && '*'}
                                </Text>
                                <OktonSearchSelect
                                  name="gerardirecionadorplano"
                                  placeholder="Sem classificação"
                                  value={formGerar.direcionadorplano}
                                  onChange={valor =>
                                    setFormGerar(f => ({
                                      ...f,
                                      direcionadorplano: valor,
                                    }))
                                  }
                                  options={[
                                    ...planosDFC.map(p => ({
                                      value: p.id_pct,
                                      label: `${p.item_pct} ${p.nome_pct}`,
                                    })),
                                  ]}
                                />
                              </Box>
                            )}

                            <Box>
                              <Text
                                as="label"
                                htmlFor="gerardepartamento"
                                display="block"
                                fontSize="sm"
                                fontWeight="medium"
                                mb="1"
                              >
                                Departamento
                              </Text>
                              <OktonSearchSelect
                                name="gerardepartamento"
                                placeholder="Sem departamento"
                                value={formGerar.departamento}
                                onChange={valor =>
                                  setFormGerar(f => ({
                                    ...f,
                                    departamento: valor,
                                  }))
                                }
                                options={[
                                  {
                                    value: DEPARTAMENTO_NOVO,
                                    label: 'Novo Departamento',
                                  },
                                  ...departamentos.map(d => ({
                                    value: d.id_dpt,
                                    label: d.nome_dpt,
                                  })),
                                ]}
                              />
                            </Box>

                            {formGerar.departamento === DEPARTAMENTO_NOVO && (
                              <Box>
                                <Text
                                  as="label"
                                  htmlFor="conciliacao-departamento-nome"
                                  display="block"
                                  fontSize="sm"
                                  fontWeight="medium"
                                  mb="1"
                                >
                                  Nome do novo Departamento *
                                </Text>
                                <OktonInput
                                  name="conciliacao-departamento-nome"
                                  color={color}
                                  size="lg"
                                  rounded="2xl"
                                  fontWeight="light"
                                  maxLength={60}
                                  value={formGerar.departamentonovo}
                                  onChange={e => {
                                    const valor = e.target.value;
                                    setFormGerar(f => ({
                                      ...f,
                                      departamentonovo: valor,
                                    }));
                                  }}
                                />
                              </Box>
                            )}

                            <Box>
                              <Text
                                as="label"
                                htmlFor="gerarcentrocusto"
                                display="block"
                                fontSize="sm"
                                fontWeight="medium"
                                mb="1"
                              >
                                Centro de Custo
                              </Text>
                              <OktonSearchSelect
                                name="gerarcentrocusto"
                                placeholder="Sem centro de custo"
                                value={formGerar.centrocusto}
                                onChange={valor =>
                                  setFormGerar(f => ({
                                    ...f,
                                    centrocusto: valor,
                                  }))
                                }
                                options={[
                                  {
                                    value: CENTRO_CUSTO_NOVO,
                                    label: 'Novo Centro de Custo',
                                  },
                                  ...centrosCusto.map(c => ({
                                    value: c.id_cdc,
                                    label: c.nome_cdc,
                                  })),
                                ]}
                              />
                            </Box>

                            {formGerar.centrocusto === CENTRO_CUSTO_NOVO && (
                              <Box>
                                <Text
                                  as="label"
                                  htmlFor="conciliacao-centrocusto-nome"
                                  display="block"
                                  fontSize="sm"
                                  fontWeight="medium"
                                  mb="1"
                                >
                                  Nome do novo Centro de Custo *
                                </Text>
                                <OktonInput
                                  name="conciliacao-centrocusto-nome"
                                  color={color}
                                  size="lg"
                                  rounded="2xl"
                                  fontWeight="light"
                                  maxLength={60}
                                  value={formGerar.centrocustonovo}
                                  onChange={e => {
                                    const valor = e.target.value;
                                    setFormGerar(f => ({
                                      ...f,
                                      centrocustonovo: valor,
                                    }));
                                  }}
                                />
                              </Box>
                            )}
                            <Box>
                              <Text
                                as="label"
                                htmlFor="conciliacao-lancamento-observacao"
                                display="block"
                                fontSize="sm"
                                fontWeight="medium"
                                mb="1"
                              >
                                Observação
                              </Text>
                              <OktonInput
                                name="conciliacao-lancamento-observacao"
                                color={color}
                                size="lg"
                                rounded="2xl"
                                fontWeight="light"
                                placeholder={
                                  activeLancamentoExtrato.descricao_exb
                                }
                                maxLength={1024}
                                value={formGerar.observacao}
                                onChange={e => {
                                  const valor = e.target.value;
                                  setFormGerar(f => ({
                                    ...f,
                                    observacao: valor,
                                  }));
                                }}
                              />
                              <Text fontSize="xs" color="gray.500" mt="1">
                                Em branco, usa o histórico do extrato.
                              </Text>
                            </Box>
                          </>
                        )}
                      </>
                    )}

                    {tipoCadastro === 'transferencia' && (
                      <>
                        <Box>
                          <Text
                            as="label"
                            htmlFor="conciliacao-transferencia-referencia"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Referência *
                          </Text>
                          <OktonInput
                            name="conciliacao-transferencia-referencia"
                            color={color}
                            size="lg"
                            rounded="2xl"
                            fontWeight="light"
                            placeholder="Nº doc / comprovante"
                            value={formTransferencia.documento}
                            onChange={e => {
                              const valor = e.target.value;
                              setFormTransferencia(f => ({
                                ...f,
                                documento: valor,
                              }));
                            }}
                          />
                        </Box>

                        <Box>
                          <Text
                            as="label"
                            htmlFor="transferenciaorigem"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Conta de Origem *
                          </Text>
                          <OktonSearchSelect
                            name="transferenciaorigem"
                            placeholder="Selecione a conta"
                            value={formTransferencia.origem}
                            onChange={valor =>
                              setFormTransferencia(f => ({
                                ...f,
                                origem: valor,
                              }))
                            }
                            options={[
                              ...contas.map(c => ({
                                value: c.id_cts,
                                label: c.descricao_cts,
                              })),
                            ]}
                          />
                        </Box>

                        <Box>
                          <Text
                            as="label"
                            htmlFor="transferenciadestino"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Conta Destino *
                          </Text>
                          <OktonSearchSelect
                            name="transferenciadestino"
                            placeholder="Selecione a conta"
                            value={formTransferencia.destino}
                            onChange={valor =>
                              setFormTransferencia(f => ({
                                ...f,
                                destino: valor,
                              }))
                            }
                            options={[
                              ...contas.map(c => ({
                                value: c.id_cts,
                                label: c.descricao_cts,
                              })),
                            ]}
                          />
                        </Box>

                        <Box>
                          <Text
                            as="label"
                            htmlFor="transferenciaplano"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Plano de Contas (DFC) {controlaDRE && '*'}
                          </Text>
                          <OktonSearchSelect
                            name="transferenciaplano"
                            placeholder="Sem classificação"
                            value={formTransferencia.planocontas}
                            onChange={valor =>
                              setFormTransferencia(f => ({
                                ...f,
                                planocontas: valor,
                              }))
                            }
                            options={[
                              ...planosDFC.map(p => ({
                                value: p.id_pct,
                                label: `${p.item_pct} ${p.nome_pct}`,
                              })),
                            ]}
                          />
                        </Box>

                        <Box>
                          <Text
                            as="label"
                            htmlFor="conciliacao-transferencia-observacao"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Observação
                          </Text>
                          <OktonInput
                            name="conciliacao-transferencia-observacao"
                            color={color}
                            size="lg"
                            rounded="2xl"
                            fontWeight="light"
                            placeholder={activeLancamentoExtrato.descricao_exb}
                            value={formTransferencia.observacao}
                            onChange={e => {
                              const valor = e.target.value;
                              setFormTransferencia(f => ({
                                ...f,
                                observacao: valor,
                              }));
                            }}
                          />
                          <Text fontSize="xs" color="gray.500" mt="1">
                            Em branco, usa o histórico do extrato.
                          </Text>
                        </Box>
                      </>
                    )}

                    {(tipoCadastro === 'saque' ||
                      tipoCadastro === 'deposito') && (
                      <>
                        {tipoCadastro === 'deposito' && (
                          <Box>
                            <Text
                              as="label"
                              htmlFor="depositomoeda"
                              display="block"
                              fontSize="sm"
                              fontWeight="medium"
                              mb="1"
                            >
                              Moeda Depositada *
                            </Text>
                            <OktonSearchSelect
                              name="depositomoeda"
                              placeholder="Selecione a forma"
                              value={formDocumento.moeda}
                              onChange={valor => {
                                setFormDocumento(f => ({
                                  ...f,
                                  moeda: valor,
                                }));
                                limpaCheques();
                              }}
                              options={[
                                ...moedasPagamento.map(m => ({
                                  value: m.id_moe,
                                  label: m.nome_moe,
                                })),
                              ]}
                            />
                          </Box>
                        )}

                        {tipoCadastro === 'deposito' && (
                          <Box>
                            <Text
                              as="label"
                              htmlFor="conciliacao-deposito-envelope"
                              display="block"
                              fontSize="sm"
                              fontWeight="medium"
                              mb="1"
                            >
                              Envelope
                            </Text>
                            <OktonInput
                              name="conciliacao-deposito-envelope"
                              color={color}
                              size="lg"
                              rounded="2xl"
                              fontWeight="light"
                              maxLength={60}
                              value={formDocumento.envelope}
                              onChange={e => {
                                const valor = e.target.value;
                                setFormDocumento(f => ({
                                  ...f,
                                  envelope: valor,
                                }));
                              }}
                            />
                          </Box>
                        )}

                        {/* Depositar cheque é escolher papéis que já existem.
                        A soma precisa fechar com o lançamento do extrato: o
                        Depósito nasce conciliado com ele. */}
                        {depositoDeCheque && (
                          <Box borderWidth="1px" rounded="2xl" p="3">
                            <Flex
                              align="center"
                              justify="space-between"
                              mb="2"
                              gridGap="2"
                            >
                              <Text fontSize="xs" color="gray.500">
                                Cheques ({chequesEscolhidos.length} de{' '}
                                {chequesFiltrados.length})
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color={color}
                              >
                                {formatValue(somaChequesEscolhidos)}
                              </Text>
                            </Flex>

                            <OktonInput
                              name="conciliacao-busca-cheques"
                              aria-label="Buscar por número, emitente ou banco"
                              size="sm"
                              rounded="lg"
                              color={color}
                              placeholder="Buscar por número, emitente ou banco"
                              value={buscaCheque}
                              onChange={e => {
                                setBuscaCheque(e.target.value);
                                // Buscar recomeça da primeira página: manter a
                                // página antiga mostraria vazio com resultado
                                // existindo.
                                setPaginaCheque(0);
                              }}
                            />

                            {chequesDaPagina.length === 0 ? (
                              <OktonEmptyState
                                title={
                                  buscaCheque.trim()
                                    ? 'Nada encontrado para a busca'
                                    : 'Nenhum cheque disponível para depositar'
                                }
                              />
                            ) : (
                              <Stack spacing="0" mt="2">
                                {chequesDaPagina.map(c => (
                                  <Flex
                                    key={c.id_fin}
                                    align="center"
                                    gridGap="2"
                                    py="2"
                                    borderBottomWidth="1px"
                                  >
                                    <Box flexShrink={0}>
                                      <OktonCheckbox
                                        name={`conciliacao-cheque-${c.id_fin}`}
                                        label={null}
                                        aria-label={`Selecionar cheque ${
                                          c.documento_fin ?? c.id_fin
                                        }`}
                                        size="md"
                                        isChecked={chequesEscolhidos.includes(
                                          c.id_fin,
                                        )}
                                        onChange={() =>
                                          alternarCheque(c.id_fin)
                                        }
                                      />
                                    </Box>
                                    <Box flex="1" minW="0">
                                      <Text
                                        fontSize="sm"
                                        color={color}
                                        whiteSpace="normal"
                                        wordBreak="normal"
                                      >
                                        {c.nomecliente_fin ||
                                          c.nome_cli ||
                                          'Sem emitente'}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        Nº {c.documento_fin ?? '—'}
                                        {c.nome_ban ? ` — ${c.nome_ban}` : ''}
                                      </Text>
                                    </Box>
                                    <Text
                                      fontSize="sm"
                                      fontWeight="semibold"
                                      color={color}
                                      whiteSpace="nowrap"
                                      flexShrink={0}
                                    >
                                      {formatValue(Number(c.valor_fin))}
                                    </Text>
                                  </Flex>
                                ))}
                              </Stack>
                            )}

                            {totalPaginasCheque > 1 && (
                              <Flex
                                align="center"
                                justify="space-between"
                                mt="2"
                                gridGap="2"
                              >
                                <Button
                                  size="xs"
                                  variant="outline"
                                  isDisabled={paginaAtualCheque === 0}
                                  onClick={() =>
                                    setPaginaCheque(paginaAtualCheque - 1)
                                  }
                                >
                                  Anterior
                                </Button>
                                <Text fontSize="xs" color="gray.500">
                                  {paginaAtualCheque + 1} de{' '}
                                  {totalPaginasCheque}
                                </Text>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  isDisabled={
                                    paginaAtualCheque >= totalPaginasCheque - 1
                                  }
                                  onClick={() =>
                                    setPaginaCheque(paginaAtualCheque + 1)
                                  }
                                >
                                  Próxima
                                </Button>
                              </Flex>
                            )}
                          </Box>
                        )}

                        <Box>
                          <Text
                            as="label"
                            htmlFor="documentoplano"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Plano de Contas (DFC) {controlaDRE && '*'}
                          </Text>
                          <OktonSearchSelect
                            name="documentoplano"
                            placeholder="Sem classificação"
                            value={formDocumento.planocontas}
                            onChange={valor =>
                              setFormDocumento(f => ({
                                ...f,
                                planocontas: valor,
                              }))
                            }
                            options={[
                              ...planosDFC.map(p => ({
                                value: p.id_pct,
                                label: `${p.item_pct} ${p.nome_pct}`,
                              })),
                            ]}
                          />
                        </Box>

                        <CaixaField
                          data={dataDoExtrato}
                          valor={formDocumento.caixa}
                          onChange={caixa =>
                            setFormDocumento(f => ({ ...f, caixa }))
                          }
                        />

                        <Box>
                          <Text
                            as="label"
                            htmlFor="conciliacao-documento-observacao"
                            display="block"
                            fontSize="sm"
                            fontWeight="medium"
                            mb="1"
                          >
                            Observação
                          </Text>
                          <OktonInput
                            name="conciliacao-documento-observacao"
                            color={color}
                            size="lg"
                            rounded="2xl"
                            fontWeight="light"
                            placeholder={activeLancamentoExtrato.descricao_exb}
                            maxLength={1024}
                            value={formDocumento.observacao}
                            onChange={e => {
                              const valor = e.target.value;
                              setFormDocumento(f => ({
                                ...f,
                                observacao: valor,
                              }));
                            }}
                          />
                          <Text fontSize="xs" color="gray.500" mt="1">
                            Em branco, usa o histórico do extrato.
                          </Text>
                        </Box>
                      </>
                    )}

                    <Button
                      colorScheme="blue"
                      leftIcon={
                        salvandoCadastro ? (
                          <Spinner size="sm" />
                        ) : tipoCadastro === 'transferencia' ? (
                          <ArrowLeftRight size={16} />
                        ) : (
                          <Link2 size={16} />
                        )
                      }
                      isDisabled={salvandoCadastro}
                      onClick={salvarCadastro}
                    >
                      Cadastrar e Conciliar
                    </Button>
                  </Stack>
                </>
              )}
            </Flex>
          </SimpleGrid>
        )
      ) : (
        /* ─── Conciliados ─── */
        <Box bg={bg} mt="1" rounded="2xl">
          {isConciliadosLoading ? (
            <Flex justify="center" py="16">
              <OktonLoadingCard h="300px" />
            </Flex>
          ) : isConciliadosError ? (
            <OktonErrorCard h="300px" onRetry={refetchConciliados} />
          ) : conciliados.length === 0 ? (
            <OktonEmptyState
              icon={Link2}
              title="Nenhuma conciliação registrada ainda"
            />
          ) : (
            <>
              {conciliadosDivergentes.length > 0 && (
                <Flex
                  px="5"
                  py="3"
                  mb="3"
                  borderWidth="1px"
                  rounded="xl"
                  align="center"
                  justify="space-between"
                  gridGap="3"
                >
                  <Text fontSize="sm">
                    {conciliadosDivergentes.length === 1
                      ? filtrandoDivergentes
                        ? 'Mostrando só a conciliação com diferença entre extrato e documento'
                        : '1 conciliação tem diferença entre extrato e documento'
                      : filtrandoDivergentes
                      ? `Mostrando só as ${conciliadosDivergentes.length} conciliações com diferença entre extrato e documento`
                      : `${conciliadosDivergentes.length} conciliações têm diferença entre extrato e documento`}
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="orange"
                    variant={filtrandoDivergentes ? 'solid' : 'outline'}
                    leftIcon={<AlertTriangle size={14} />}
                    onClick={() => setSomenteDivergentes(!filtrandoDivergentes)}
                  >
                    {filtrandoDivergentes
                      ? 'Mostrar todas'
                      : 'Somente com diferença'}
                  </Button>
                </Flex>
              )}
              <OktonDataGrid
                rows={conciliadosVisiveis}
                columns={[
                  ...colunasDataGrid(
                    COLUNAS_CONCILIADOS,
                    ordenacaoConciliados,
                    alternarConciliados,
                    {
                      extrato: m => (
                        <>
                          <Text fontWeight="medium" isTruncated maxW="180px">
                            {m.descricao_exb}
                          </Text>
                          <Text fontSize="xs" color="gray.400" mt="0.5">
                            {formatDataExtrato(m.data_exb)} • {m.descricao_cts}
                          </Text>
                        </>
                      ),
                      elo: () => (
                        <Box color="gray.300">
                          <Link2 size={14} />
                        </Box>
                      ),
                      documento: m => {
                        // Do outro lado pode estar um título, uma
                        // Transferência, um Saque ou um Depósito.
                        const documento = descreveDocumento(m);
                        return (
                          <>
                            <Text fontWeight="medium" isTruncated maxW="180px">
                              {documento.titulo}
                            </Text>
                            <Text fontSize="xs" color="gray.400" mt="0.5">
                              {m.tipo_cbc === TIPO_FINANCEIRO
                                ? `${
                                    m.baixa_fin
                                      ? formatCustomDate(
                                          m.baixa_fin.slice(0, 10),
                                          'yyyy-MM-dd',
                                          'dd/MM/yyyy',
                                        )
                                      : ''
                                  }${
                                    m.centrocusto_fin
                                      ? ` • ${m.centrocusto_fin}`
                                      : ''
                                  }`
                                : documento.detalhe}
                            </Text>
                          </>
                        );
                      },
                      valor_exb: m => (
                        <Text
                          fontWeight="semibold"
                          whiteSpace="nowrap"
                          color={
                            Number(m.valor_exb) >= 0 ? 'green.600' : 'red.500'
                          }
                        >
                          {formatValue(Number(m.valor_exb))}
                        </Text>
                      ),
                      valor_doc: m => {
                        // valor_exb vem com sinal e o total dos documentos é
                        // sempre positivo: comparar direto acusaria diferença
                        // em todo lançamento de saída. O total soma todos os
                        // documentos do extrato, o mesmo critério do filtro
                        // de divergências.
                        const diff = Math.abs(
                          Math.abs(Number(m.valor_exb)) -
                            Number(m.totaldocumentos_cbc),
                        );
                        return (
                          <>
                            <Text
                              fontWeight="semibold"
                              whiteSpace="nowrap"
                              color={
                                Number(m.valor_exb) >= 0
                                  ? 'green.600'
                                  : 'red.500'
                              }
                            >
                              {formatValue(valorDoDocumento(m))}
                            </Text>
                            {diff > 0.01 && (
                              <Text fontSize="xs" color="orange.500" mt="0.5">
                                Dif: {formatValue(diff)}
                              </Text>
                            )}
                          </>
                        );
                      },
                    },
                  ),
                  {
                    key: 'acoes',
                    header: null,
                    align: 'right',
                    render: m => (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        title="Desfazer vínculo"
                        onClick={() => handleUnmatch(m)}
                      >
                        <Unlink size={16} />
                      </Button>
                    ),
                  },
                ]}
                getRowId={m => m.id_cbc}
                page={paginaConciliados + 1}
                pageSize={tamanhoPaginaConciliados}
                total={
                  filtrandoDivergentes
                    ? conciliadosDivergentes.length
                    : conciliados.length
                }
                pageSizeOptions={TAMANHOS_PAGINA}
                onPageChange={nova => setPaginaConciliados(nova - 1)}
                onPageSizeChange={setTamanhoPaginaConciliados}
              />
            </>
          )}
        </Box>
      )}

      <OktonConfirmDialog
        isOpen={confirmarAutoMatch}
        onClose={() => {
          if (!autoMatchMutation.isLoading) setConfirmarAutoMatch(false);
        }}
        onConfirm={executarAutoMatch}
        title="Conciliar automaticamente"
        description={`Conciliar automaticamente a conta ${
          contaFiltro?.descricao_cts ?? ''
        }? Serão percorridos os ${
          lancamentosExtrato.length
        } lançamentos pendentes do extrato. Cada um será vinculado a um documento de mesmo valor, mesmo sentido e com data até 5 dias da do extrato: título já baixado, Saque, Depósito ou Transferência. Título em aberto não entra. Um documento só é usado uma vez e o que não tiver par continua pendente. Os vínculos criados podem ser desfeitos individualmente na aba Conciliados.`}
        confirmLabel="Conciliar automaticamente"
        intent="primary"
        isLoading={autoMatchMutation.isLoading}
      />

      {/* Margem de baixo das colunas de Conciliação */}
      <Text mb="2" mt="6" />
    </BankAppPage>
  );
};

export default Conciliacao;
